// Language support
export type Language = 'en' | 'sv' | 'es' | 'de';

export const translations = {
  en: {
    legend: 'Legend',
    // --- i18n additions ---
    app_successfullyUpgradedTo: "Successfully upgraded to",
    app_successfullyDowngradedTo: "Successfully downgraded to",
    app_failedToChangeSubscription: "Failed to change subscription",
    app_unknownError: "Unknown error",
    app_reportThankYou: "Thank you for your report. We will review it shortly.",
    app_reportAlreadyReported: "This location has already been reported. Thank you for your concern!",
    app_reportError: "There was an error submitting your report. Please try again.",
    app_away: "away",
    app_hoursNotAvailable: "Hours not available",
    app_website: "Website",
    app_drive: "Drive",
    app_geoUnableRetrieve: "Unable to retrieve your location.",
    app_geoPermissionDenied: "Location access denied. Please enable location permissions in your browser.",
    app_geoUnavailable: "Location information unavailable. Please try again.",
    app_geoTimeout: "Location request timed out. Please try again.",
    app_geoErrorLabel: "Location error",
    app_geoErrorTitle: "Location Error",
    app_geoDefaultLocationNote: "Using Uppsala area as default location. Please enable location permissions for accurate positioning.",
    app_geoFailedGetLocation: "Failed to get your location. Please try again.",
    app_geoNotSupported: "Geolocation is not supported by your browser.",
    app_tryAgainFewMinutes: "Please try again in a few minutes.",
    app_errorLabel: "Error",
    app_aboutWhere2: "About Where2",
    app_aboutText: "The team behind Where2 has been EV owners for over 10 years, experiencing firsthand the challenges of finding quality food options while charging on the road. Too often, drivers arrive at a supercharger and can't see any restaurants from the parking lot—or end up at places that don't meet expectations. Where2 was created to solve this problem: helping EV drivers discover the best nearby restaurants with real reviews, photos, and walking directions, so they can make the most of their charging stops and enjoy their journey.",
    app_welcomeBack: "Welcome back",
    app_readyToFindFood: "Ready to find great food while charging?",
    app_welcomeBackReady: "Welcome back! Ready to find great restaurants while you charge?",
    app_plusFeatureUpgrade: "Plus feature - Click to upgrade",
    app_getSuggestions: "Get suggestions",
    app_backToSuperchargerView: "Back to supercharger view",
    app_closeMap: "Close map",
    app_loadingMap: "Loading map...",
    app_loadingChargers: "Loading chargers...",
    app_findingSuperchargers: "Finding superchargers in your area",
    app_fetchingStations: "Fetching charging stations...",
    app_slowConnectionNote: "This may take a moment on slower connections",
    app_zoomIn: "Zoom in (centered on your location)",
    app_zoomOut: "Zoom out (centered on your location)",
    app_chooseYourPreference: "Choose your preference",
    app_prefNormal: "Normal preferences",
    app_prefNicer: "A little nicer",
    app_prefCheaper: "A little cheaper",
    app_searching: "Searching...",
    app_startSearch: "Start Search",
    app_blueCircleNote: "We'll search for restaurants near chargers within the blue circle",
    app_found: "Found",
    app_restaurantsWord: "restaurants",
    app_higherQuality: "(higher quality)",
    app_budgetFriendly: "(budget-friendly)",
    app_unknownCharger: "Unknown Charger",
    app_seeAllRestaurants: "See all restaurants",
    app_and: "and",
    app_moreRestaurants: "more restaurants...",
    app_noRestaurantsInArea: "No restaurants found in the selected area. Try zooming out or moving the map.",
    app_loading: "Loading...",
    app_close: "Close",
    app_learnTitle: "The Future of EV Travel",
    app_learnRealityTitle: "The New Reality of EV Charging",
    app_learnRealityText: "Nowadays charging time has reduced quite a lot and to make the most of a journey it's sometimes good to be well prepared to eat when arriving at a charger location. This app will let you move the restaurant consideration so that it can be decided which suits the travelers best even before arriving at the charger.",
    app_learnPreOrderTitle: "Plan Your Meal in Advance",
    app_learnPreOrderText: "In fact, why not decide on the right restaurant before you even arrive? This transforms your charging stop from a waiting period into a seamless part of your journey. No more rushing to find food or settling for whatever's available - you can plan your perfect meal in advance.",
    app_learnHowTitle: "How Where2 Works",
    app_learnStep1Title: "Find Charging Stations:",
    app_learnStep1Text: "Use your location or search for charging stations along your route. Where2 supports Tesla Superchargers, Ionity, OKQ8, and other networks.",
    app_learnStep2Title: "Discover Restaurants:",
    app_learnStep2Text: "See all nearby restaurants with real-time information including ratings, photos, opening hours, and walking directions from the charger.",
    app_learnStep3Title: "Plan Your Stop:",
    app_learnStep3Text: "Compare options, check distances, and even get walking directions to make the most of your charging time.",
    app_learnVisionTitle: "The Future Vision",
    app_learnVisionText: "In the future, Where2 will evolve to include restaurant preferences, dietary requirements, and even integration with restaurant ordering systems. Imagine being able to set your food preferences and have Where2 suggest the best charging stops based on your culinary interests. The app will learn your preferences over time, making each journey more personalized and enjoyable.",
    app_learnBenefitsTitle: "Key Benefits:",
    app_benefitTimeTitle: "Time Efficiency:",
    app_benefitTimeText: "Plan your meal before arriving, not after",
    app_benefitChoicesTitle: "Better Choices:",
    app_benefitChoicesText: "See all options before arriving",
    app_benefitStressTitle: "Stress Reduction:",
    app_benefitStressText: "No more rushing to find food",
    app_benefitJourneyTitle: "Journey Enhancement:",
    app_benefitJourneyText: "Turn charging stops into enjoyable breaks",
    app_benefitFutureTitle: "Future-Ready:",
    app_benefitFutureText: "Platform for restaurant preferences and discovery",
    login_title: "Login",
    login_emailLabel: "Email",
    login_emailPlaceholder: "Enter your email address",
    login_passwordLabel: "Password",
    login_submit: "Login",
    login_cancel: "Cancel",
    login_switchToRegister: "Don't have an account? Register",
    register_title: "Register",
    register_trialTitle: "Free Plus Trial!",
    register_trialTextBefore: "Get",
    register_trialTextAfter: "of premium access with all features unlocked. After your trial expires, your account will become a free limited account. You can upgrade to a paid subscription at any time to keep premium features.",
    register_unitMinutes: "minutes",
    register_unitHours: "hours",
    register_unitDays: "days",
    register_emailLabel: "Email",
    register_emailPlaceholder: "Enter your email address",
    register_displayNameLabel: "Display Name",
    register_displayNamePlaceholder: "Enter your display name",
    register_passwordLabel: "Password",
    register_confirmPasswordLabel: "Confirm Password",
    register_couponLabel: "Coupon Code (Optional)",
    register_couponPlaceholder: "Enter coupon code if you have one",
    register_submit: "Register",
    register_cancel: "Cancel",
    register_switchToLogin: "Already have an account? Login",
    verify_errorNoToken: "No verification token provided",
    verify_successDefault: "Email verified successfully!",
    verify_errorFailed: "Verification failed",
    verify_errorNetwork: "Network error. Please try again.",
    verify_loadingTitle: "Verifying Email",
    verify_loadingText: "Please wait while we verify your email address...",
    verify_successTitle: "Email Verified!",
    verify_continue: "Continue to Where2",
    verify_failedTitle: "Verification Failed",
    verify_goHome: "Go to Homepage",
    verify_registerAgain: "Register Again",
    faq_close: "Close",
    faq_title: "Frequently Asked Questions",
    faq_q1Question: "So what's all this about?",
    faq_q1Answer: "Just a way to be somewhat more prepared for your charging stop",
    faq_q2Question: "I don't understand! What do I do?",
    faq_q2Answer: "No problem! On the home page, click 'Show me how it works' for a quick walkthrough — or just tap 'Find restaurants' to see the chargers near you and the restaurants within walking distance of each one.",
    faq_q3Question: "I don't get it. I don't see any restaurants!",
    faq_q3Answer: "This could of course be one of those nasty bugs, but please also check if you have configured the settings the way you want them. Here you can set your restaurant preferences and how long you are willing to walk when you are charging. You can also select price levels and other filtering options that help you find the best choice for you.",
    faq_q4Question: "Why isn't this free? It should be a service!",
    faq_q4Answer: "Unfortunately the data source we use is also not free meaning that usage of the application comes with a sometimes high price tag depending on the volume. We try however to keep the price for the different options at a minimum to cover expenses even at high load.",
    faq_q5Question: "Why are there different subscription tiers?",
    faq_q5AnswerPart1: "We keep it simple: a ",
    faq_freeTier: "Free",
    faq_q5AnswerPart2: " tier for basic restaurant discovery, and a single paid ",
    faq_plusTier: "Plus",
    faq_q5AnswerPart3: " tier that unlocks everything — restaurant photos and reviews, advanced filtering, route optimization, the \"suggest a meal\" feature, and additional search types (hotels, shopping).",
    faq_q6Question: "Isn't this really a luxury problem which doesn't need a solution",
    faq_q6Answer: "Yes of course, but this is something that the creators of the site always have found a time thief and an inconvenience as charger sites are very different and have very different options so being prepared is never bad!",
    faq_q7Question: "How am I supposed to use this in the car?",
    faq_q7Answer: "We are working on a Carplay version. Hopefully we will have it avaiable shortly. In the meantime we suggest using the mobile phone or maybe a tablet if available. Some EVs have integrated web browser functionality and there it should work well. Remember to not use the application while driving!",
    faq_q8Question: "I want to also find hotels and shopping near the charger. Can't you show that also?",
    faq_q8Answer: "We are working on these options also. Please check the news section for updates on this.",
    faq_q9Question: "Don't you have some nice AI integration where I can tell the application what I want?",
    faq_q9Answer: "We have tried out a few alternatives around this but found that the results are unreliable at best and mostly just silly and inaccurate. Therefore we have used the old fashioned way to try to help you find a good restaurant near a charger.",
    faq_q10Question: "Where do the reviews come from?",
    faq_q10Answer: "We use Google Places as the review source. We may change this in the future. Perhaps even start working with the userbase of the application for reviews, but for now Google users are the judges.",
    faq_q11Question: "What do I do if I find something that doesn't work?",
    faq_q11AnswerPart1: "Please ",
    faq_contactUsHere: "contact us here",
    faq_q11AnswerPart2: " with any problems you experience. We are here to help!",
    faq_q12Question: "What do I do if I want a new feature?",
    faq_q12AnswerPart1: "Same here. Please ",
    faq_contactUs: "contact us",
    faq_q12AnswerPart2: " and we will see what we can do!",
    faq_q13Question: "How does Where2 work?",
    faq_q13Answer: "Where2 helps EV drivers find restaurants near charging stations. Simply click \"Find a Restaurant\" to find nearby superchargers and see restaurant options within walking distance.",
    faq_q14Question: "Which charging networks are supported?",
    faq_q14Answer: "Currently we support Tesla Superchargers, Ionity, and OKQ8. You can select which networks to display in your settings.",
    faq_q15Question: "How far can I search for restaurants?",
    faq_q15Answer: "You can adjust the restaurant search radius from 0.2km to 1.6km in your settings. The default is 0.6km for optimal walking distance.",
    faq_q16Question: "How far can I search for superchargers?",
    faq_q16Answer: "You can adjust the supercharger search radius from 20km to 400km in your settings. The default is 100km.",
    faq_q17Question: "Do I need to pay to use Where2?",
    faq_q17AnswerPart1: "Where2 has a ",
    faq_q17AnswerPart2: " tier plus one paid subscription: ",
    faq_q17AnswerPart3: " (monthly $3 or yearly $30) unlocks restaurant photos and reviews, advanced filtering, route optimization, the \"suggest a meal\" feature, and hotel & shopping search.",
    faq_q18Question: "How accurate is the restaurant information?",
    faq_q18Answer: "We use Google Places API to provide real-time restaurant data including ratings, photos, opening hours, and contact information.",
    faq_q19Question: "Can I get walking directions to restaurants?",
    faq_q19Answer: "Yes! Click the \"🚶 Walking Route\" link next to any restaurant to get Google Maps walking directions from the supercharger to the restaurant.",
    faq_q20Question: "Is my location data stored?",
    faq_q20Answer: "No, we don't store your location data. Your location is only used temporarily to find nearby superchargers and restaurants.",
    about_close: "Close",
    about_title: "About Where2",
    about_body: "The team behind Where2 has been EV owners for over 10 years, experiencing firsthand the challenges of finding quality food options while charging on the road. Too often, drivers arrive at a supercharger and can't see any restaurants from the parking lot—or end up at places that don't meet expectations. Where2 was created to solve this problem: helping EV drivers discover the best nearby restaurants with real reviews, photos, and walking directions, so they can make the most of their charging stops and enjoy their journey.",
    learnMore_close: "Close",
    learnMore_title: "The Future of EV Travel",
    learnMore_section1Title: "The New Reality of EV Charging",
    learnMore_section1Body: "Nowadays charging time has reduced quite a lot and to make the most of a journey it's sometimes good to be well prepared to eat when arriving at a charger location. This app will let you move the restaurant consideration so that it can be decided which suits the travelers best even before arriving at the charger.",
    learnMore_section2Title: "Plan Your Meal in Advance",
    learnMore_section2Body: "In fact, why not decide on the right restaurant before you even arrive? This transforms your charging stop from a waiting period into a seamless part of your journey. No more rushing to find food or settling for whatever's available - you can plan your perfect meal in advance.",
    learnMore_section3Title: "How Where2 Works",
    learnMore_step1Label: "Find Charging Stations:",
    learnMore_step1Body: " Use your location or search for charging stations along your route. Where2 supports Tesla Superchargers, Ionity, OKQ8, and other networks.",
    learnMore_step2Label: "Discover Restaurants:",
    learnMore_step2Body: " See all nearby restaurants with real-time information including ratings, photos, opening hours, and walking directions from the charger.",
    learnMore_step3Label: "Plan Your Stop:",
    learnMore_step3Body: " Compare options, check distances, and even get walking directions to make the most of your charging time.",
    learnMore_benefitsTitle: "Key Benefits:",
    learnMore_benefit1Label: "Time Efficiency:",
    learnMore_benefit1Body: " Plan your meal before arriving, not after",
    learnMore_benefit2Label: "Better Choices:",
    learnMore_benefit2Body: " See all options before arriving",
    learnMore_benefit3Label: "Stress Reduction:",
    learnMore_benefit3Body: " No more rushing to find food",
    learnMore_benefit4Label: "Journey Enhancement:",
    learnMore_benefit4Body: " Turn charging stops into enjoyable breaks",
    learnMore_benefit5Label: "Future-Ready:",
    learnMore_benefit5Body: " Platform for restaurant preferences and discovery",
    contact_close: "Close",
    contact_title: "Contact Us",
    contact_subtitle: "Have a question? We'd love to hear from you!",
    contact_nameLabel: "Name *",
    contact_namePlaceholder: "Your name",
    contact_emailLabel: "Email *",
    contact_emailPlaceholder: "your.email@example.com",
    contact_subjectLabel: "Subject *",
    contact_subjectPlaceholder: "What's your question about?",
    contact_messageLabel: "Message *",
    contact_messagePlaceholder: "Tell us more about your question...",
    contact_successMessage: "Thank you! Your message has been sent successfully. We'll get back to you soon.",
    contact_errorMessage: "Sorry, there was an error sending your message. Please try again.",
    contact_cancel: "Cancel",
    contact_sending: "Sending...",
    contact_sendMessage: "Send Message",
    cookie_title: "Cookie Notice",
    cookie_body1: "We use cookies to maintain your authenticated session while you use our service. These cookies are essential for the website to function properly and provide you with a secure experience.",
    cookie_importantLabel: "Important:",
    cookie_importantText: " We do not use cookies to track you between websites or for any cross-site tracking activities. Our cookies are strictly limited to session management and authentication on our platform.",
    cookie_storeLabel: "What we store:",
    cookie_storeText: " Session tokens for login authentication only",
    cookie_dontLabel: "What we don't do:",
    cookie_dontText: " Cross-site tracking, advertising, or data sharing",
    cookie_acceptCookies: "Accept Cookies",
    cookie_decline: "Decline",
    cookie_rememberNote: "Your choice will be remembered for future visits",
    disclaimer_close: "Close",
    disclaimer_title: "Safety Disclaimer",
    disclaimer_safetyBody: "Please note that this feature may not be used by the driver when the vehicle is not parked. It should be used by a passenger or at a vehicle stand still. The usage of this app website is entirely restricted to safe conditions and no liability will be accepted if failing to comply with these restrictions or any other unsecure usage conditions.",
    nav_manageSubscription: "Click to manage your subscription",
    nav_mapCache: "Map Cache",
    nav_tiles: "tiles",
    nav_saved: "saved",
    nav_signOut: "Sign Out",
    mainBtn_plusFeatureUpgrade: "Plus feature - Click to upgrade",
    legend_stalls: "stalls",
    legend_naStalls: "N/A stalls",
    legend_fromYourLocation: "from your location",
    legend_drive: "drive",
    legend_arriveAt: "Arrive at",
    legend_favoriteRestaurant: "Favorite restaurant",
    map_favoriteChargerTooltip: "Your favorite restaurant is nearby",
    legend_hoursNotAvailable: "Hours not available",
    legend_priceBudget: "Budget",
    legend_priceModerate: "Moderate",
    legend_priceExpensive: "Expensive",
    legend_priceVeryExpensive: "Very Expensive",
    legend_reviews: "reviews",
    legend_web: "Web",
    legend_call: "Call",
    legend_directions: "Directions",
    legend_reportTooltip: "Report if this is not actually a restaurant",
    legend_notARestaurant: "Not a restaurant!",
    floatLegend_stalls: "stalls",
    floatLegend_naStalls: "N/A stalls",
    floatLegend_openInGoogleMaps: "Open in Google Maps",
    floatLegend_maps: "Maps",
    floatLegend_closeLegend: "Close legend",
    floatLegend_selectedCharger: "Selected Charger",
    popup_closePopup: "Close popup",
    popup_supercharger: "Supercharger",
    popup_chargingStation: "Charging Station",
    popup_restaurantsFoundNearby: "restaurants found nearby",
    popup_loginToSeeDetails: "Login to see restaurant details",
    popup_loadingRestaurants: "Loading restaurants...",
    restCard_away: "away",
    restCard_hoursNotAvailable: "Hours not available",
    restCard_website: "Website",
    restCard_call: "Call",
    restCard_directions: "Directions",
    restCard_teslaNav: "Tesla Nav",
    proxActions_wait1h: "Wait 1h",
    proxActions_wait2h: "Wait 2h",
    proxActions_turnOffAll: "Turn Off All Notifications",
    proxNotif_away: "away",
    proxNotif_greatRestaurantsNearby: "great restaurants nearby - perfect for a meal break!",
    proxNotif_topRestaurants: "Top Restaurants:",
    proxNotif_clicked: "Clicked!",
    proxNotif_showMe: "Show Me",
    proxNotif_close: "Close",
    chargerSearch_min3chars: "Please enter at least 3 characters to search",
    chargerSearch_searchFailed: "Search failed",
    chargerSearch_locationSearchFailed: "Location search failed",
    chargerSearch_chargerSearchFailed: "Charger search failed",
    chargerSearch_unknown: "Unknown",
    chargerSearch_title: "Search Chargers",
    chargerSearch_placeholder: "Search for chargers by city or location (e.g., Stockholm, Sollentuna)...",
    chargerSearch_typeMin3: "Type at least 3 characters to search",
    chargerSearch_searchingAround: "Searching around:",
    chargerSearch_noChargersFound: "No chargers found for",
    chargerSearch_tryDifferent: "Try searching for a different city or location",
    chargerSearch_na: "N/A",
    chargerSearch_chargingPoints: "charging points",
    chargerSearch_more: "more",
    chargerSearch_selectCharger: "Select Charger",
    chargerSearch_findPerfectStop: "Find Your Perfect Charging Stop",
    chargerSearch_emptyStateDesc: "Search for chargers by city or location to find the best places to charge and dine.",
    report_title: "Report Non-Restaurant",
    report_aboutGoogleData: "About Google Places Data",
    report_para1: "Google's API sometimes categorizes non-restaurant businesses (like gas stations, shops, or services) as restaurants. This can happen when these businesses serve food as a secondary service.",
    report_para2: "If you believe this location is not primarily a restaurant, please let us know so we can improve our recommendations.",
    report_reasonLabel: "Why is this not a restaurant? (Optional)",
    report_reasonPlaceholder: "e.g., 'This is a gas station that only sells snacks' or 'This is a grocery store, not a restaurant'",
    report_cancel: "Cancel",
    report_submitting: "Submitting...",
    report_submit: "Submit Report",
    mealPlan_south: "SOUTH",
    mealPlan_north: "NORTH",
    mealPlan_latitudeStable: "LATITUDE STABLE",
    mealPlan_west: "WEST",
    mealPlan_east: "EAST",
    mealPlan_title: "MEAL PLAN GENERATED!",
    mealPlan_close: "Close",
    mealPlan_locationHistory: "LOCATION HISTORY",
    mealPlan_travelDirection: "TRAVEL DIRECTION",
    mealPlan_vector: "Vector:",
    mealPlan_direction: "Direction:",
    mealPlan_superchargersInDirection: "SUPERCHARGERS IN DIRECTION",
    mealPlan_foundInDirection: "Found in travel direction:",
    mealPlan_selectedForPlan: "Selected for meal plan:",
    mealPlan_top: "Top",
    mealPlan_byDistance: "by distance",
    mealPlan_mealPlanHeading: "MEAL PLAN",
    mealPlan_away: "away",
    mealPlan_minTravelTime: "min travel time",
    mealPlan_fromCharger: "from charger",
    mealPlan_hoursNotAvailable: "Hours not available",
    mealPlan_fromCache: "(from cache)",
    mealPlan_noRestaurantsFound: "No restaurants found",
    mealPlan_noRestaurantsDesc: "No restaurants were found at the selected superchargers.",
    mealPlan_jensTestMode: "JENS TEST MODE ACTIVE",
    mealPlan_simulatedTravel: "Simulated southward travel: Stockholm → South",
    suggestMeal_waitForMap: "Please wait for the map to load completely before searching.",
    suggestMeal_noChargers: "No charging stations found in the area or nearby. Try a different location or zoom out further.",
    suggestMeal_searchFailed: "Search failed. Please try again.",
    suggestMeal_prefNormal: "Normal preferences",
    suggestMeal_prefNicer: "A little nicer",
    suggestMeal_prefCheaper: "A little cheaper",
    suggestMeal_descNormal: "Balanced recommendations based on ratings",
    suggestMeal_descNicer: "Premium chargers & restaurants with 4.0+ ratings",
    suggestMeal_descCheaper: "Budget-friendly options with 3.5+ ratings",
    suggestMeal_title: "Select Area for Meal Suggestions",
    suggestMeal_close: "Close",
    suggestMeal_fromYourLocation: "from your location",
    suggestMeal_estimated: "(estimated)",
    suggestMeal_minDrive: "min drive from here",
    suggestMeal_arriveAt: "Arrive at",
    suggestMeal_backToSearch: "Back to search area",
    suggestMeal_loadingMap: "Loading map...",
    suggestMeal_searching: "Searching...",
    suggestMeal_startSearch: "Start Search",
    suggestMeal_dragInstruction: "Drag the map to position the blue circle over your desired search area, then click Start",
    miniMap_close: "Close",
    miniMap_map: "Map:",
    miniMap_sendToTesla: "Send to Tesla App",
    miniMap_teslaNavigation: "Tesla Navigation",
    miniMap_nav: "Nav",
    miniMap_googleMapsNav: "Google Maps Navigation",
    miniMap_maps: "Maps",
    miniMap_drive: "Drive",
    settings_title: "Settings",
    settings_subtitle: "Customize your experience",
    settings_restaurantRadius: "Restaurant Search Radius",
    settings_chargerRadius: "EV Charger Search Radius",
    settings_chargerNetworks: "Charger Networks",
    settings_unitSystem: "Unit System",
    settings_metric: "Metric (km, °C)",
    settings_imperial: "Imperial (mi, °F)",
    settings_save: "Save Settings",
    settings_cancel: "Cancel",
    settings_profileSettings: "Profile Settings",
    settings_emailAddress: "Email Address",
    settings_emailPlaceholder: "Email address",
    settings_fullName: "Full Name",
    settings_fullNamePlaceholder: "Enter your full name",
    settings_searchPreferences: "Search Preferences",
    settings_current: "Current",
    settings_evChargingNetworks: "EV Charging Networks",
    settings_displayPreferences: "Display Preferences",
    settings_metricUnits: "Metric Units",
    settings_usingMetric: "Using kilometers and Celsius",
    settings_usingImperial: "Using miles and Fahrenheit",
    settings_currencyUSD: "US Dollar",
    settings_currencyEUR: "Euro",
    settings_currencySEK: "Swedish Krona",
    settings_currencyDKK: "Danish Krone",
    settings_currencyNOK: "Norwegian Krone",
    settings_currencyCHF: "Swiss Franc",
    settings_currency: "Currency",
    settings_foodPreferences: "Food Preferences",
    settings_priceLevelsChains: "Price levels & chains",
    settings_subscription: "Subscription",
    settings_manageYourPlan: "Manage your plan",
    settings_saving: "Saving...",
    prefs_close: "Close",
    prefs_title: "Restaurant Preferences",
    prefs_loading: "Loading preferences...",
    prefs_preferredChains: "Preferred Restaurant Chains",
    prefs_loadingChains: "Loading restaurant chains...",
    prefs_customKeywords: "Custom Keywords",
    prefs_keywordPlaceholder: "e.g., hamburgare, pizza, sushi...",
    prefs_add: "Add",
    prefs_priceLevels: "Price Levels",
    prefs_priceLevelsDesc: "Select the price ranges you prefer for restaurants",
    prefs_price1_label: "Budget-friendly",
    prefs_price1_desc: "Inexpensive ($)",
    prefs_price2_label: "Moderate",
    prefs_price2_desc: "Moderate ($$)",
    prefs_price3_label: "Expensive",
    prefs_price3_desc: "Expensive ($$$)",
    prefs_price4_label: "Very Expensive",
    prefs_price4_desc: "Very Expensive ($$$$)",
    prefs_searchConfiguration: "Search Configuration",
    prefs_searchLevel_include_all_name: "Include all superchargers (default)",
    prefs_searchLevel_include_all_desc: "Show all chargers, highlight preferred restaurants with gold outline",
    prefs_searchLevel_show_favorites_only_name: "Show my favorite restaurants only",
    prefs_searchLevel_show_favorites_only_desc: "Show all chargers but only display restaurants matching preferences",
    prefs_searchLevel_favorites_only_chargers_name: "Only chargers with favorite restaurants",
    prefs_searchLevel_favorites_only_chargers_desc: "Only show chargers that have nearby restaurants matching preferences",
    prefs_savedSuccess: "Preferences saved successfully!",
    prefs_saving: "Saving...",
    prefs_savePreferences: "Save Preferences",
    prefs_cancel: "Cancel",
    prefs_errorLoad: "Failed to load preferences",
    prefs_errorSave: "Failed to save preferences",
    prefs_unknownError: "Unknown error occurred",
    restCount_foundSingular: "restaurant found",
    restCount_foundPlural: "restaurants found",
    restCount_near: "near",
    restCount_signupPrompt: "Sign up to see restaurant details, photos, reviews, and more!",
    restCount_unlockFullDetails: "Unlock Full Details",
    restCount_freeToSignup: "Free to sign up • Plus features available",
    restCount_plusFeaturesInclude: "Plus features include:",
    restCount_featureRatings: "Restaurant ratings & reviews",
    restCount_featurePrices: "Price levels & cuisine types",
    restCount_featureNavigation: "Navigation & directions",
    pricing_errorCalculating: "Error calculating subscription change: ",
    pricing_failedCalculate: "Failed to calculate subscription change",
    pricing_changeSuccess: "Subscription changed successfully!",
    pricing_errorChanging: "Error changing subscription: ",
    pricing_failedChange: "Failed to change subscription",
    pricing_plusPlanTitle: "Plus Plan",
    pricing_plusPlanSubtitle: "Everything you need for EV road trips",
    pricing_close: "Close",
    pricing_choosePlan: "Choose Your Plan",
    pricing_pricesShownIn: "Prices shown in",
    pricing_mostPopular: "Most Popular",
    pricing_monthly: "monthly",
    pricing_yearly: "yearly",
    pricing_save: "Save",
    pricing_month: "month",
    pricing_year: "year",
    pricing_monthBilledYearly: "month when billed yearly",
    pricing_activePlan: "Active Plan",
    pricing_calculating: "Calculating...",
    pricing_processing: "Processing...",
    pricing_changePlan: "Change Plan",
    pricing_choose: "Choose",
    pricing_currentSubStatus: "Current Subscription Status",
    pricing_hideHistory: "Hide History",
    pricing_viewHistory: "View History",
    pricing_planLabel: "Plan:",
    pricing_statusLabel: "Status:",
    pricing_expiresLabel: "Expires:",
    pricing_recentBillingHistory: "Recent Billing History",
    pricing_noBillingHistory: "No billing history yet.",
    pricing_confirmSubChange: "Confirm Subscription Change",
    pricing_currentPlanLabel: "Current Plan:",
    pricing_newPlanLabel: "New Plan:",
    pricing_daysUsed: "Days Used:",
    pricing_of: "of",
    pricing_daysRemaining: "Days Remaining:",
    pricing_proratedRefund: "Prorated Refund:",
    pricing_noCharge: "No Charge",
    pricing_netCharge: "Net Charge",
    pricing_descriptionLabel: "Description:",
    pricing_cancel: "Cancel",
    pricing_confirmChange: "Confirm Change",
    subMgmt_title: "Subscription Management",
    subMgmt_currentSubscription: "Current Subscription",
    subMgmt_monthly: "Monthly",
    subMgmt_yearly: "Yearly",
    subMgmt_statusLabel: "Status:",
    subMgmt_expiresLabel: "Expires:",
    subMgmt_priceLabel: "Price:",
    subMgmt_choosePlan: "Choose Your Plan",
    subMgmt_plusPlans: "Plus Plans",
    subMgmt_currentPlan: "Current Plan",
    subMgmt_save: "Save",
    subMgmt_month: "month",
    subMgmt_select: "Select",
    subMgmt_footer: "All plans include access to our EV charging station network and restaurant discovery features. Cancel or change your plan at any time.",
    tierChange_upgrade: "Upgrade",
    tierChange_downgrade: "Downgrade",
    tierChange_subscription: "Subscription",
    tierChange_importantNotice: "Important Notice",
    tierChange_downgradeWarning: "Downgrading your subscription will reduce your available features. Some functionality may become unavailable immediately after the change.",
    tierChange_aboutToUpgrade: "You're about to upgrade from:",
    tierChange_aboutToDowngrade: "You're about to downgrade from:",
    tierChange_month: "month",
    tierChange_monthlyPriceChange: "Monthly price change:",
    tierChange_upgradeBillingInfo: "Your subscription will be upgraded immediately. You'll be charged the prorated difference for the current billing period.",
    tierChange_downgradeBillingInfo: "Your subscription will be downgraded immediately. Any unused portion will be credited to your next billing cycle.",
    tierChange_cancel: "Cancel",
    tierChange_processing: "Processing...",
    tierChange_confirm: "Confirm",
    // Navigation
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    settings: 'Settings',
    about: 'About',
    pricing: 'Pricing',
    faq: 'FAQ',
    contact: 'Contact',
    
    // Hero Section
    heroTitle: 'Find the Best Meal Near Your EV Charger',
    heroSubtitle: 'Discover top-rated restaurants within walking distance of your charging stop. Where2 helps you plan your meal at superchargers, showing you the best places to eat nearby—filtered by your preferences and reviews—so you can enjoy a great meal while your car charges.',
    getStarted: "Let's go!",
    learnMore: 'Show me how it works',
    
    // Features
    smartLocationSearch: 'Search Chargers',
    smartLocationDesc: 'Search for charging stations by location and find great restaurants nearby.',
    searchChargers: 'Search Chargers',
    preOrderReady: 'Meal Suggestions',
    preOrderDesc: 'Use this feature to select a map area where you are heading and where you would like to have restaurant suggestions depending on your preferences.',
    restaurantDiscovery: 'Restaurant Discovery',
    restaurantDesc: 'Explore new food experiences with detailed reviews and photos near an EV charger along your route.',
    
    // CTA Section
    ctaTitle: 'Ready to find your next favorite place to eat?',
    ctaSubtitle: 'Plan your meal before you arrive.',
    
    // Success Stories
    successStories: 'Success Stories',
    successStoriesDesc: 'See how Where2 is transforming the EV dining experience',
    shareYourStory: 'Share Your Story',
    
    // Main Button
    findNearbyRestaurants: 'Find restaurants',
    planMyMeal: '🚗 Plan my meal',
    
    // Modals
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    couponCode: 'Coupon Code (Optional)',
    enterEmail: 'Enter your email address',
    enterCoupon: 'Enter coupon code if you have one',
    dontHaveAccount: "Don't have an account? Register",
    
    // Settings
    yourLocationIcon: 'Your Location Icon',
    priceRangePreferences: 'Price Range Preferences',
    maxRestaurants: 'Max Restaurants to Show',
    
    // Restaurant Popup
    nearestRestaurants: 'Nearest Restaurants:',
    open: 'Open',
    closed: 'Closed',
    km: 'km',
    call: 'Call',
    directions: 'Directions',
    map: 'Map',
    showMap: 'Show map with restaurants',
    close: 'Close',
    
    // Disclaimer
    disclaimerTitle: 'Safety Disclaimer',
    disclaimerText: 'Please note that this feature may not be used by the driver when the vehicle is not parked. It should be used by a passenger or at a vehicle stand still. The usage of this app website is entirely restricted to safe conditions and no liability will be accepted if failing to comply with these restrictions or any other unsecure usage conditions.',
    iUnderstand: 'I Understand - Continue',
    doNotShowAgain: 'Do not show this disclaimer again',
    importantSafetyNotice: 'Important Safety Notice',
    safeUsageGuidelines: 'Safe Usage Guidelines:',
    onlyUseWhenParked: 'Only use when parked: Ensure your vehicle is completely stopped and parked',
    passengerUse: 'Passenger use: Let passengers handle the app while driving',
    safeEnvironment: 'Safe environment: Use only in safe, well-lit areas',
    followTrafficLaws: 'Follow traffic laws: Always comply with local traffic regulations',
    stayAlert: 'Stay alert: Remain aware of your surroundings',
    cancel: 'Cancel',
    disclaimerAcknowledgment: 'By proceeding, you acknowledge that you have read and understood this safety disclaimer and agree to use the app responsibly.',
    settingsNote: 'You can change this setting later in your account settings.',
    browserNote: 'This preference will be saved in your browser.',
    
    // Learn More
    learnMoreTitle: 'About Where2',
    learnMoreText: 'Nowadays charging time has reduced quite a lot and to make the most of a journey it\'s sometimes good to be well prepared to eat when arriving at a charger location. This app will let you move the restaurant consideration so that it can be decided which suites the travellers best even before arriving at the charger. This way you can plan your perfect meal in advance... and then some more technical description on how the app works and that in the future it will be possible to add restaurant preferences and similar to find the best chargers',
    
    // Contact
    contactTitle: 'Contact Us',
    contactDesc: 'Have a question or feedback? We\'d love to hear from you!',
    name: 'Name',
    subject: 'Subject',
    message: 'Message',
    sendMessage: 'Send Message',
    enterName: 'Enter your name',
    enterSubject: 'Enter subject',
    enterMessage: 'Enter your message',
    messageSent: 'Message sent successfully!',
    messageError: 'Failed to send message. Please try again.',
    sending: 'Sending...',
    
    // Auth Errors
    emailPasswordRequired: 'Email and password are required',
    invalidEmail: 'Please enter a valid email address',
    passwordLength: 'Password must be between 6 and 128 characters',
    passwordsDontMatch: 'Passwords do not match',
    userExists: 'User already exists',
    invalidCredentials: 'Invalid email or password',
    
    // Trial
    trialActive: 'Trial Active',
    trialExpired: 'Trial Expired',
    trialDaysLeft: 'days left',
    trialHoursLeft: 'hours left',
    
    // Subscription
    subscription: 'Subscription',
    free: 'Free',
    premium: 'Plus',
    expired: 'Expired',
    active: 'Active',
    
    // General
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    save: 'Save',
    ok: 'OK',
    yes: 'Yes',
    no: 'No',
  },
  sv: {
    legend: 'Teckenförklaring',
    // --- i18n additions ---
    app_successfullyUpgradedTo: "Uppgraderad till",
    app_successfullyDowngradedTo: "Nedgraderad till",
    app_failedToChangeSubscription: "Det gick inte att ändra prenumerationen",
    app_unknownError: "Okänt fel",
    app_reportThankYou: "Tack för din rapport! Vi tittar på den inom kort.",
    app_reportAlreadyReported: "Den här platsen är redan rapporterad. Tack för att du hör av dig!",
    app_reportError: "Något gick fel när rapporten skulle skickas. Försök igen.",
    app_away: "bort",
    app_hoursNotAvailable: "Öppettider saknas",
    app_website: "Webbplats",
    app_drive: "Kör",
    app_geoUnableRetrieve: "Vi kunde inte hämta din plats.",
    app_geoPermissionDenied: "Åtkomst till plats nekad. Tillåt platsdelning i din webbläsare.",
    app_geoUnavailable: "Din plats är inte tillgänglig just nu. Försök igen.",
    app_geoTimeout: "Det tog för lång tid att hämta din plats. Försök igen.",
    app_geoErrorLabel: "Platsfel",
    app_geoErrorTitle: "Platsfel",
    app_geoDefaultLocationNote: "Vi använder Uppsalaområdet som standardplats. Tillåt platsdelning för att få exakt position.",
    app_geoFailedGetLocation: "Vi kunde inte hämta din plats. Försök igen.",
    app_geoNotSupported: "Din webbläsare har inte stöd för platsdelning.",
    app_tryAgainFewMinutes: "Försök igen om några minuter.",
    app_errorLabel: "Fel",
    app_aboutWhere2: "Om Where2",
    app_aboutText: "Teamet bakom Where2 har kört elbil i över tio år och vet av egen erfarenhet hur svårt det kan vara att hitta bra mat medan bilen laddar. Alltför ofta rullar man in till en laddstation utan att se en enda restaurang från parkeringen – eller så hamnar man på ett ställe som inte alls motsvarar förväntningarna. Där kommer Where2 in: vi hjälper dig som kör elbil att hitta de bästa restaurangerna i närheten, med äkta recensioner, foton och gångvägar, så att du får ut mesta möjliga av varje laddstopp och verkligen njuter av resan.",
    app_welcomeBack: "Välkommen tillbaka",
    app_readyToFindFood: "Redo att hitta god mat medan du laddar?",
    app_welcomeBackReady: "Välkommen tillbaka! Redo att hitta en bra restaurang medan du laddar?",
    app_plusFeatureUpgrade: "Plus-funktion – klicka för att uppgradera",
    app_getSuggestions: "Få förslag",
    app_backToSuperchargerView: "Tillbaka till laddstationsvyn",
    app_closeMap: "Stäng kartan",
    app_loadingMap: "Laddar kartan...",
    app_loadingChargers: "Laddar laddstationer...",
    app_findingSuperchargers: "Söker laddstationer i ditt område",
    app_fetchingStations: "Hämtar laddstationer...",
    app_slowConnectionNote: "Det här kan ta en stund om uppkopplingen är långsam",
    app_zoomIn: "Zooma in (centrerat på din plats)",
    app_zoomOut: "Zooma ut (centrerat på din plats)",
    app_chooseYourPreference: "Välj dina önskemål",
    app_prefNormal: "Vanliga önskemål",
    app_prefNicer: "Lite finare",
    app_prefCheaper: "Lite billigare",
    app_searching: "Söker...",
    app_startSearch: "Starta sökning",
    app_blueCircleNote: "Vi söker efter restauranger nära laddstationerna inom den blå cirkeln",
    app_found: "Hittade",
    app_restaurantsWord: "restauranger",
    app_higherQuality: "(högre kvalitet)",
    app_budgetFriendly: "(prisvärt)",
    app_unknownCharger: "Okänd laddstation",
    app_seeAllRestaurants: "Se alla restauranger",
    app_and: "och",
    app_moreRestaurants: "fler restauranger...",
    app_noRestaurantsInArea: "Vi hittade inga restauranger i det valda området. Prova att zooma ut eller flytta kartan.",
    app_loading: "Laddar...",
    app_close: "Stäng",
    app_learnTitle: "Framtidens elbilsresor",
    app_learnRealityTitle: "En ny verklighet för elbilsladdning",
    app_learnRealityText: "Idag går laddningen betydligt snabbare än förr, och för att få ut mesta möjliga av resan lönar det sig att redan på förhand ha en plan för var man ska äta när man kommer fram. Med den här appen flyttar du restaurangvalet framåt i tiden – du bestämmer vilket ställe som passar er bäst redan innan du rullar in till laddstationen.",
    app_learnPreOrderTitle: "Planera måltiden i förväg",
    app_learnPreOrderText: "Varför inte bestämma restaurang redan innan du är framme? Då förvandlas laddstoppet från spilltid till en självklar del av resan. Slut på stressigt letande efter mat och att tvingas nöja sig med det som råkar finnas – nu planerar du den perfekta måltiden i lugn och ro.",
    app_learnHowTitle: "Så fungerar Where2",
    app_learnStep1Title: "Hitta laddstationer:",
    app_learnStep1Text: "Utgå från din plats eller sök efter laddstationer längs rutten. Where2 har stöd för Tesla Superchargers, Ionity, OKQ8 och andra nätverk.",
    app_learnStep2Title: "Upptäck restauranger:",
    app_learnStep2Text: "Se alla restauranger i närheten med aktuell information – betyg, foton, öppettider och gångväg från laddstationen.",
    app_learnStep3Title: "Planera stoppet:",
    app_learnStep3Text: "Jämför alternativen, kolla avstånden och få gångvägen dit, så att du får ut mesta möjliga av laddtiden.",
    app_learnVisionTitle: "Vår vision framåt",
    app_learnVisionText: "Framöver kommer Where2 att växa med restaurangönskemål, kosthänsyn och till och med koppling till restaurangernas beställningssystem. Tänk dig att ange vad du gillar att äta och låta Where2 föreslå de bästa laddstoppen utifrån din smak. Appen lär känna dina preferenser med tiden och gör varje resa lite mer personlig och trevlig.",
    app_learnBenefitsTitle: "De största fördelarna:",
    app_benefitTimeTitle: "Tidseffektivt:",
    app_benefitTimeText: "Planera måltiden innan du kommer fram – inte efter",
    app_benefitChoicesTitle: "Bättre val:",
    app_benefitChoicesText: "Se alla alternativ redan innan du är framme",
    app_benefitStressTitle: "Mindre stress:",
    app_benefitStressText: "Slut på stressigt letande efter mat",
    app_benefitJourneyTitle: "En bättre resa:",
    app_benefitJourneyText: "Gör laddstoppen till sköna pauser",
    app_benefitFutureTitle: "Redo för framtiden:",
    app_benefitFutureText: "En plattform för restaurangönskemål och upptäckter",
    login_title: "Logga in",
    login_emailLabel: "E-post",
    login_emailPlaceholder: "Ange din e-postadress",
    login_passwordLabel: "Lösenord",
    login_submit: "Logga in",
    login_cancel: "Avbryt",
    login_switchToRegister: "Har du inget konto? Registrera dig",
    register_title: "Skapa konto",
    register_trialTitle: "Gratis Plus-period!",
    register_trialTextBefore: "Få",
    register_trialTextAfter: "premiumåtkomst med alla funktioner upplåsta. När provperioden tar slut övergår kontot till en gratisversion med begränsade funktioner. Du kan uppgradera till ett betalt abonnemang när som helst för att behålla premiumfunktionerna.",
    register_unitMinutes: "minuter",
    register_unitHours: "timmar",
    register_unitDays: "dagar",
    register_emailLabel: "E-post",
    register_emailPlaceholder: "Ange din e-postadress",
    register_displayNameLabel: "Visningsnamn",
    register_displayNamePlaceholder: "Ange ditt visningsnamn",
    register_passwordLabel: "Lösenord",
    register_confirmPasswordLabel: "Bekräfta lösenord",
    register_couponLabel: "Rabattkod (valfritt)",
    register_couponPlaceholder: "Ange din rabattkod om du har en",
    register_submit: "Skapa konto",
    register_cancel: "Avbryt",
    register_switchToLogin: "Har du redan ett konto? Logga in",
    verify_errorNoToken: "Ingen verifieringstoken angavs",
    verify_successDefault: "Din e-postadress är verifierad!",
    verify_errorFailed: "Verifieringen misslyckades",
    verify_errorNetwork: "Nätverksfel. Försök igen.",
    verify_loadingTitle: "Verifierar e-post",
    verify_loadingText: "Vänta medan vi verifierar din e-postadress...",
    verify_successTitle: "E-posten är verifierad!",
    verify_continue: "Fortsätt till Where2",
    verify_failedTitle: "Verifieringen misslyckades",
    verify_goHome: "Gå till startsidan",
    verify_registerAgain: "Skapa konto igen",
    faq_close: "Stäng",
    faq_title: "Vanliga frågor",
    faq_q1Question: "Vad handlar det här om, egentligen?",
    faq_q1Answer: "Ett enkelt sätt att vara lite bättre förberedd inför laddstoppet.",
    faq_q2Question: "Jag förstår inte riktigt – vad gör jag?",
    faq_q2Answer: "Inga problem! Klicka på 'Visa hur det fungerar' på startsidan för en snabb genomgång – eller tryck bara på 'Hitta restauranger' för att se laddstationerna nära dig och restaurangerna inom gångavstånd från varje station.",
    faq_q3Question: "Jag fattar inte – jag ser inga restauranger!",
    faq_q3Answer: "Det kan förstås röra sig om en av de där tråkiga buggarna, men kolla gärna också att du har ställt in allt som du vill ha det. Under inställningarna anger du dina restaurangönskemål och hur långt du är beredd att gå medan bilen laddar. Du kan också välja prisnivåer och andra filter som hjälper dig att hitta det som passar dig bäst.",
    faq_q4Question: "Varför är det här inte gratis? Borde det inte vara en självklar tjänst?",
    faq_q4Answer: "Tyvärr är datakällan vi bygger på inte gratis, och beroende på hur mycket appen används kan notan bli rejält hög. Vi gör vårt bästa för att hålla priset på de olika alternativen så lågt som möjligt – precis så att det täcker kostnaderna, även när trycket är högt.",
    faq_q5Question: "Varför finns det olika prenumerationsnivåer?",
    faq_q5AnswerPart1: "Vi håller det enkelt: en ",
    faq_freeTier: "Free",
    faq_q5AnswerPart2: "-nivå för grundläggande restaurangsökning och en enda betald ",
    faq_plusTier: "Plus",
    faq_q5AnswerPart3: "-nivå som låser upp allt – restaurangfoton och recensioner, avancerad filtrering, ruttoptimering, funktionen \"föreslå en måltid\" och fler söktyper (hotell och shopping).",
    faq_q6Question: "Är inte det här egentligen ett lyxproblem som inte behöver någon lösning?",
    faq_q6Answer: "Jodå, men det är ändå något som vi bakom sidan alltid har tyckt varit en tidstjuv och ett gnäll. Laddplatser ser väldigt olika ut och har väldigt olika utbud – så att vara förberedd skadar aldrig!",
    faq_q7Question: "Hur ska jag använda det här i bilen?",
    faq_q7Answer: "Vi jobbar på en CarPlay-version och hoppas ha den på plats snart. Tills dess rekommenderar vi mobilen eller en surfplatta. Vissa elbilar har en inbyggd webbläsare, och där fungerar appen fint. Kom ihåg: använd aldrig appen medan du kör!",
    faq_q8Question: "Jag vill också hitta hotell och shopping nära laddaren. Kan ni inte visa det?",
    faq_q8Answer: "Det jobbar vi på. Håll utkik i nyhetssektionen för uppdateringar.",
    faq_q9Question: "Har ni ingen smart AI där jag bara kan berätta vad jag är sugen på?",
    faq_q9Answer: "Vi har testat några olika lösningar, men resultaten var i bästa fall opålitliga och oftast rätt tokiga och felaktiga. Därför gör vi det på gammaldags vis i stället, för att på riktigt hjälpa dig hitta en bra restaurang nära laddaren.",
    faq_q10Question: "Var kommer recensionerna ifrån?",
    faq_q10Answer: "Vi hämtar recensionerna från Google Places. Det kan komma att ändras framöver – kanske börjar vi till och med samla in omdömen från appens egna användare. Men just nu är det Google-användarna som är domarna.",
    faq_q11Question: "Vad gör jag om jag hittar något som inte fungerar?",
    faq_q11AnswerPart1: "Du får gärna ",
    faq_contactUsHere: "kontakta oss här",
    faq_q11AnswerPart2: " om något krånglar. Vi finns här för att hjälpa till!",
    faq_q12Question: "Vad gör jag om jag önskar en ny funktion?",
    faq_q12AnswerPart1: "Samma sak där – ",
    faq_contactUs: "kontakta oss",
    faq_q12AnswerPart2: " så ska vi se vad vi kan göra!",
    faq_q13Question: "Hur fungerar Where2?",
    faq_q13Answer: "Where2 hjälper dig som kör elbil att hitta restauranger nära laddstationer. Klicka bara på \"Hitta en restaurang\" så letar vi upp laddstationer i närheten och visar restaurangerna inom gångavstånd.",
    faq_q14Question: "Vilka laddnätverk stöds?",
    faq_q14Answer: "Just nu har vi stöd för Tesla Superchargers, Ionity och OKQ8. Du väljer själv vilka nätverk som ska visas under inställningarna.",
    faq_q15Question: "Hur långt bort kan jag söka efter restauranger?",
    faq_q15Answer: "Du kan ställa in sökradien för restauranger mellan 0,2 km och 1,6 km under inställningarna. Standard är 0,6 km, ett lagom gångavstånd.",
    faq_q16Question: "Hur långt bort kan jag söka efter laddstationer?",
    faq_q16Answer: "Du kan ställa in sökradien för laddstationer mellan 20 km och 400 km under inställningarna. Standard är 100 km.",
    faq_q17Question: "Måste jag betala för att använda Where2?",
    faq_q17AnswerPart1: "Where2 har en ",
    faq_q17AnswerPart2: "-nivå plus en betald prenumeration: ",
    faq_q17AnswerPart3: " (3 USD/månad eller 30 USD/år) låser upp restaurangfoton och recensioner, avancerad filtrering, ruttoptimering, funktionen \"föreslå en måltid\" samt sökning efter hotell och shopping.",
    faq_q18Question: "Hur tillförlitlig är restauranginformationen?",
    faq_q18Answer: "Vi använder Google Places API och hämtar aktuell restaurangdata i realtid – betyg, foton, öppettider och kontaktuppgifter.",
    faq_q19Question: "Kan jag få en gångväg till restaurangerna?",
    faq_q19Answer: "Ja! Klicka på länken \"🚶 Gångväg\" bredvid en restaurang så får du en gångvägsbeskrivning i Google Maps från laddstationen till restaurangen.",
    faq_q20Question: "Sparar ni min platsinformation?",
    faq_q20Answer: "Nej, vi sparar inte din platsinformation. Den används bara tillfälligt för att hitta laddstationer och restauranger i närheten.",
    about_close: "Stäng",
    about_title: "Om Where2",
    about_body: "Teamet bakom Where2 har kört elbil i över tio år och vet av egen erfarenhet hur svårt det kan vara att hitta bra mat medan bilen laddar. Alltför ofta rullar man in till en laddstation utan att se en enda restaurang från parkeringen – eller så hamnar man på ett ställe som inte alls motsvarar förväntningarna. Där kommer Where2 in: vi hjälper dig som kör elbil att hitta de bästa restaurangerna i närheten, med äkta recensioner, foton och gångvägar, så att du får ut mesta möjliga av varje laddstopp och verkligen njuter av resan.",
    learnMore_close: "Stäng",
    learnMore_title: "Framtidens elbilsresor",
    learnMore_section1Title: "En ny verklighet för elbilsladdning",
    learnMore_section1Body: "Idag går laddningen betydligt snabbare än förr, och för att få ut mesta möjliga av resan lönar det sig att redan på förhand ha en plan för var man ska äta när man kommer fram. Med den här appen flyttar du restaurangvalet framåt i tiden – du bestämmer vilket ställe som passar er bäst redan innan du rullar in till laddstationen.",
    learnMore_section2Title: "Planera måltiden i förväg",
    learnMore_section2Body: "Varför inte bestämma restaurang redan innan du är framme? Då förvandlas laddstoppet från spilltid till en självklar del av resan. Slut på stressigt letande efter mat och att tvingas nöja sig med det som råkar finnas – nu planerar du den perfekta måltiden i lugn och ro.",
    learnMore_section3Title: "Så fungerar Where2",
    learnMore_step1Label: "Hitta laddstationer:",
    learnMore_step1Body: " Utgå från din plats eller sök efter laddstationer längs rutten. Where2 har stöd för Tesla Superchargers, Ionity, OKQ8 och andra nätverk.",
    learnMore_step2Label: "Upptäck restauranger:",
    learnMore_step2Body: " Se alla restauranger i närheten med aktuell information – betyg, foton, öppettider och gångväg från laddstationen.",
    learnMore_step3Label: "Planera stoppet:",
    learnMore_step3Body: " Jämför alternativen, kolla avstånden och få gångvägen dit, så att du får ut mesta möjliga av laddtiden.",
    learnMore_benefitsTitle: "De största fördelarna:",
    learnMore_benefit1Label: "Tidseffektivt:",
    learnMore_benefit1Body: " Planera måltiden innan du kommer fram – inte efter",
    learnMore_benefit2Label: "Bättre val:",
    learnMore_benefit2Body: " Se alla alternativ redan innan du är framme",
    learnMore_benefit3Label: "Mindre stress:",
    learnMore_benefit3Body: " Slut på stressigt letande efter mat",
    learnMore_benefit4Label: "En bättre resa:",
    learnMore_benefit4Body: " Gör laddstoppen till sköna pauser",
    learnMore_benefit5Label: "Redo för framtiden:",
    learnMore_benefit5Body: " En plattform för restaurangönskemål och upptäckter",
    contact_close: "Stäng",
    contact_title: "Kontakta oss",
    contact_subtitle: "Har du en fråga? Vi hör gärna från dig!",
    contact_nameLabel: "Namn *",
    contact_namePlaceholder: "Ditt namn",
    contact_emailLabel: "E-post *",
    contact_emailPlaceholder: "din.adress@exempel.se",
    contact_subjectLabel: "Ämne *",
    contact_subjectPlaceholder: "Vad gäller din fråga?",
    contact_messageLabel: "Meddelande *",
    contact_messagePlaceholder: "Berätta lite mer...",
    contact_successMessage: "Tack! Ditt meddelande är skickat. Vi återkommer så snart vi kan.",
    contact_errorMessage: "Något gick tyvärr fel när meddelandet skulle skickas. Försök igen.",
    contact_cancel: "Avbryt",
    contact_sending: "Skickar...",
    contact_sendMessage: "Skicka meddelande",
    cookie_title: "Om cookies",
    cookie_body1: "Vi använder cookies för att hålla dig inloggad medan du använder tjänsten. De är nödvändiga för att webbplatsen ska fungera och för att ge dig en säker upplevelse.",
    cookie_importantLabel: "Viktigt:",
    cookie_importantText: " Vi använder inte cookies för att spåra dig mellan olika webbplatser. Våra cookies är strikt begränsade till sessionshantering och inloggning på vår plattform.",
    cookie_storeLabel: "Det här sparar vi:",
    cookie_storeText: " Endast sessionstoken för inloggning",
    cookie_dontLabel: "Det här gör vi inte:",
    cookie_dontText: " Spårning mellan webbplatser, annonsering eller datadelning",
    cookie_acceptCookies: "Acceptera cookies",
    cookie_decline: "Avböj",
    cookie_rememberNote: "Vi kommer ihåg ditt val till nästa besök",
    disclaimer_close: "Stäng",
    disclaimer_title: "Säkerhetsinformation",
    disclaimer_safetyBody: "Tänk på att den här funktionen inte får användas av föraren när fordonet är i rörelse. Den ska användas av en passagerare eller när bilen står stilla. Appen får bara användas under säkra förhållanden, och vi tar inget ansvar om dessa villkor eller andra säkerhetsföreskrifter inte följs.",
    nav_manageSubscription: "Klicka för att hantera din prenumeration",
    nav_mapCache: "Kartcache",
    nav_tiles: "rutor",
    nav_saved: "sparade",
    nav_signOut: "Logga ut",
    mainBtn_plusFeatureUpgrade: "Plus-funktion – klicka för att uppgradera",
    legend_stalls: "platser",
    legend_naStalls: "Antal platser okänt",
    legend_fromYourLocation: "från din plats",
    legend_drive: "körning",
    legend_arriveAt: "Framme",
    legend_favoriteRestaurant: "Favoritrestaurang",
    map_favoriteChargerTooltip: "En favoritrestaurang finns i närheten",
    legend_hoursNotAvailable: "Öppettider saknas",
    legend_priceBudget: "Budget",
    legend_priceModerate: "Måttlig",
    legend_priceExpensive: "Dyr",
    legend_priceVeryExpensive: "Mycket dyr",
    legend_reviews: "recensioner",
    legend_web: "Webb",
    legend_call: "Ring",
    legend_directions: "Vägbeskrivning",
    legend_reportTooltip: "Rapportera om det här inte är en restaurang",
    legend_notARestaurant: "Inte en restaurang!",
    floatLegend_stalls: "platser",
    floatLegend_naStalls: "Antal platser okänt",
    floatLegend_openInGoogleMaps: "Öppna i Google Maps",
    floatLegend_maps: "Karta",
    floatLegend_closeLegend: "Stäng teckenförklaringen",
    floatLegend_selectedCharger: "Vald laddstation",
    popup_closePopup: "Stäng popup",
    popup_supercharger: "Supercharger",
    popup_chargingStation: "Laddstation",
    popup_restaurantsFoundNearby: "restauranger hittades i närheten",
    popup_loginToSeeDetails: "Logga in för att se restaurangdetaljer",
    popup_loadingRestaurants: "Laddar restauranger...",
    restCard_away: "bort",
    restCard_hoursNotAvailable: "Öppettider saknas",
    restCard_website: "Webbplats",
    restCard_call: "Ring",
    restCard_directions: "Vägbeskrivning",
    restCard_teslaNav: "Tesla Nav",
    proxActions_wait1h: "Vänta 1 h",
    proxActions_wait2h: "Vänta 2 h",
    proxActions_turnOffAll: "Stäng av alla aviseringar",
    proxNotif_away: "bort",
    proxNotif_greatRestaurantsNearby: "toppenrestauranger i närheten – perfekt för en matpaus!",
    proxNotif_topRestaurants: "Toppenrestauranger:",
    proxNotif_clicked: "Klickat!",
    proxNotif_showMe: "Visa mig",
    proxNotif_close: "Stäng",
    chargerSearch_min3chars: "Skriv minst 3 tecken för att söka",
    chargerSearch_searchFailed: "Sökningen misslyckades",
    chargerSearch_locationSearchFailed: "Platssökningen misslyckades",
    chargerSearch_chargerSearchFailed: "Laddarsökningen misslyckades",
    chargerSearch_unknown: "Okänd",
    chargerSearch_title: "Sök laddare",
    chargerSearch_placeholder: "Sök efter laddare på stad eller plats (t.ex. Stockholm, Sollentuna)...",
    chargerSearch_typeMin3: "Skriv minst 3 tecken för att söka",
    chargerSearch_searchingAround: "Söker kring:",
    chargerSearch_noChargersFound: "Inga laddare hittades för",
    chargerSearch_tryDifferent: "Prova att söka på en annan stad eller plats",
    chargerSearch_na: "Ej tillgängligt",
    chargerSearch_chargingPoints: "laddpunkter",
    chargerSearch_more: "till",
    chargerSearch_selectCharger: "Välj laddare",
    chargerSearch_findPerfectStop: "Hitta ditt perfekta laddstopp",
    chargerSearch_emptyStateDesc: "Sök efter laddare på stad eller plats för att hitta de bästa ställena att ladda och äta på.",
    report_title: "Rapportera felaktig restaurang",
    report_aboutGoogleData: "Om data från Google Places",
    report_para1: "Googles API klassar ibland verksamheter som inte är restauranger – till exempel bensinstationer, butiker eller andra tjänster – som restauranger. Det händer när de serverar mat vid sidan av sin huvudverksamhet.",
    report_para2: "Om du tror att den här platsen egentligen inte är en restaurang får du gärna säga till, så att vi kan göra våra rekommendationer bättre.",
    report_reasonLabel: "Varför är det här inte en restaurang? (Valfritt)",
    report_reasonPlaceholder: "t.ex. 'Det här är en bensinstation som bara säljer snacks' eller 'Det här är en livsmedelsbutik, inte en restaurang'",
    report_cancel: "Avbryt",
    report_submitting: "Skickar...",
    report_submit: "Skicka rapport",
    mealPlan_south: "SÖDER",
    mealPlan_north: "NORR",
    mealPlan_latitudeStable: "LATITUD STABIL",
    mealPlan_west: "VÄSTER",
    mealPlan_east: "ÖSTER",
    mealPlan_title: "MÅLTIDSPLAN KLAR!",
    mealPlan_close: "Stäng",
    mealPlan_locationHistory: "PLATSHISTORIK",
    mealPlan_travelDirection: "FÄRDRIKTNING",
    mealPlan_vector: "Vektor:",
    mealPlan_direction: "Riktning:",
    mealPlan_superchargersInDirection: "LADDSTATIONER I FÄRDRIKTNINGEN",
    mealPlan_foundInDirection: "Hittade i färdriktningen:",
    mealPlan_selectedForPlan: "Valda för måltidsplanen:",
    mealPlan_top: "Topp",
    mealPlan_byDistance: "efter avstånd",
    mealPlan_mealPlanHeading: "MÅLTIDSPLAN",
    mealPlan_away: "bort",
    mealPlan_minTravelTime: "min restid",
    mealPlan_fromCharger: "från laddstationen",
    mealPlan_hoursNotAvailable: "Öppettider saknas",
    mealPlan_fromCache: "(från cache)",
    mealPlan_noRestaurantsFound: "Inga restauranger hittades",
    mealPlan_noRestaurantsDesc: "Vi hittade inga restauranger vid de valda laddstationerna.",
    mealPlan_jensTestMode: "JENS TESTLÄGE AKTIVT",
    mealPlan_simulatedTravel: "Simulerad resa söderut: Stockholm → Söder",
    suggestMeal_waitForMap: "Vänta tills kartan har laddats klart innan du söker.",
    suggestMeal_noChargers: "Vi hittade inga laddstationer i området eller i närheten. Prova en annan plats eller zooma ut lite.",
    suggestMeal_searchFailed: "Sökningen misslyckades. Försök igen.",
    suggestMeal_prefNormal: "Vanliga önskemål",
    suggestMeal_prefNicer: "Lite finare",
    suggestMeal_prefCheaper: "Lite billigare",
    suggestMeal_descNormal: "Balanserade förslag baserade på betyg",
    suggestMeal_descNicer: "Finare laddstopp och restauranger med minst 4,0 i betyg",
    suggestMeal_descCheaper: "Prisvärda alternativ med minst 3,5 i betyg",
    suggestMeal_title: "Välj område för måltidsförslag",
    suggestMeal_close: "Stäng",
    suggestMeal_fromYourLocation: "från din plats",
    suggestMeal_estimated: "(uppskattat)",
    suggestMeal_minDrive: "min körning härifrån",
    suggestMeal_arriveAt: "Framme",
    suggestMeal_backToSearch: "Tillbaka till sökområdet",
    suggestMeal_loadingMap: "Laddar kartan...",
    suggestMeal_searching: "Söker...",
    suggestMeal_startSearch: "Starta sökning",
    suggestMeal_dragInstruction: "Dra i kartan så att den blå cirkeln hamnar över området du vill söka i, och klicka sedan på Starta",
    miniMap_close: "Stäng",
    miniMap_map: "Karta:",
    miniMap_sendToTesla: "Skicka till Tesla-appen",
    miniMap_teslaNavigation: "Tesla-navigering",
    miniMap_nav: "Nav",
    miniMap_googleMapsNav: "Google Maps-navigering",
    miniMap_maps: "Karta",
    miniMap_drive: "Kör",
    settings_title: "Inställningar",
    settings_subtitle: "Anpassa din upplevelse",
    settings_restaurantRadius: "Sökradie för restauranger",
    settings_chargerRadius: "Sökradie för laddstationer",
    settings_chargerNetworks: "Laddnätverk",
    settings_unitSystem: "Enhetssystem",
    settings_metric: "Metriskt (km, °C)",
    settings_imperial: "Imperiskt (mi, °F)",
    settings_save: "Spara inställningar",
    settings_cancel: "Avbryt",
    settings_profileSettings: "Profilinställningar",
    settings_emailAddress: "E-postadress",
    settings_emailPlaceholder: "E-postadress",
    settings_fullName: "Fullständigt namn",
    settings_fullNamePlaceholder: "Ange ditt fullständiga namn",
    settings_searchPreferences: "Sökinställningar",
    settings_current: "Nuvarande",
    settings_evChargingNetworks: "Laddnätverk för elbilar",
    settings_displayPreferences: "Visningsinställningar",
    settings_metricUnits: "Metriska enheter",
    settings_usingMetric: "Använder kilometer och Celsius",
    settings_usingImperial: "Använder miles och Fahrenheit",
    settings_currencyUSD: "US-dollar",
    settings_currencyEUR: "Euro",
    settings_currencySEK: "Svensk krona",
    settings_currencyDKK: "Dansk krona",
    settings_currencyNOK: "Norsk krona",
    settings_currencyCHF: "Schweizerfranc",
    settings_currency: "Valuta",
    settings_foodPreferences: "Matpreferenser",
    settings_priceLevelsChains: "Prisnivåer och kedjor",
    settings_subscription: "Prenumeration",
    settings_manageYourPlan: "Hantera ditt abonnemang",
    settings_saving: "Sparar...",
    prefs_close: "Stäng",
    prefs_title: "Restaurangönskemål",
    prefs_loading: "Laddar önskemål...",
    prefs_preferredChains: "Restaurangkedjor du gillar",
    prefs_loadingChains: "Laddar restaurangkedjor...",
    prefs_customKeywords: "Egna sökord",
    prefs_keywordPlaceholder: "t.ex. hamburgare, pizza, sushi...",
    prefs_add: "Lägg till",
    prefs_priceLevels: "Prisnivåer",
    prefs_priceLevelsDesc: "Välj de prisnivåer du föredrar",
    prefs_price1_label: "Budgetvänligt",
    prefs_price1_desc: "Billigt ($)",
    prefs_price2_label: "Måttligt",
    prefs_price2_desc: "Måttligt ($$)",
    prefs_price3_label: "Dyrt",
    prefs_price3_desc: "Dyrt ($$$)",
    prefs_price4_label: "Mycket dyrt",
    prefs_price4_desc: "Mycket dyrt ($$$$)",
    prefs_searchConfiguration: "Sökinställningar",
    prefs_searchLevel_include_all_name: "Inkludera alla laddstationer (standard)",
    prefs_searchLevel_include_all_desc: "Visa alla laddstationer och markera dina favoritrestauranger med guldkant",
    prefs_searchLevel_show_favorites_only_name: "Visa bara mina favoritrestauranger",
    prefs_searchLevel_show_favorites_only_desc: "Visa alla laddstationer, men bara de restauranger som matchar dina önskemål",
    prefs_searchLevel_favorites_only_chargers_name: "Bara laddstationer med favoritrestauranger",
    prefs_searchLevel_favorites_only_chargers_desc: "Visa bara laddstationer som har restauranger i närheten som matchar dina önskemål",
    prefs_savedSuccess: "Dina önskemål är sparade!",
    prefs_saving: "Sparar...",
    prefs_savePreferences: "Spara önskemål",
    prefs_cancel: "Avbryt",
    prefs_errorLoad: "Det gick inte att ladda dina önskemål",
    prefs_errorSave: "Det gick inte att spara dina önskemål",
    prefs_unknownError: "Ett okänt fel inträffade",
    restCount_foundSingular: "restaurang hittad",
    restCount_foundPlural: "restauranger hittade",
    restCount_near: "nära",
    restCount_signupPrompt: "Skapa ett konto för att se restaurangdetaljer, foton, recensioner och mycket mer!",
    restCount_unlockFullDetails: "Lås upp alla detaljer",
    restCount_freeToSignup: "Gratis att skapa konto • Plus-funktioner finns tillgängliga",
    restCount_plusFeaturesInclude: "I Plus ingår:",
    restCount_featureRatings: "Restaurangbetyg och recensioner",
    restCount_featurePrices: "Prisnivåer och kökstyper",
    restCount_featureNavigation: "Navigering och vägbeskrivningar",
    pricing_errorCalculating: "Fel vid beräkning av prenumerationsändringen: ",
    pricing_failedCalculate: "Det gick inte att beräkna prenumerationsändringen",
    pricing_changeSuccess: "Prenumerationen har ändrats!",
    pricing_errorChanging: "Fel vid ändring av prenumerationen: ",
    pricing_failedChange: "Det gick inte att ändra prenumerationen",
    pricing_plusPlanTitle: "Plus-plan",
    pricing_plusPlanSubtitle: "Allt du behöver för elbilsresan",
    pricing_close: "Stäng",
    pricing_choosePlan: "Välj din plan",
    pricing_pricesShownIn: "Priser visas i",
    pricing_mostPopular: "Populärast",
    pricing_monthly: "månadsvis",
    pricing_yearly: "årsvis",
    pricing_save: "Spara",
    pricing_month: "månad",
    pricing_year: "år",
    pricing_monthBilledYearly: "månad vid årsbetalning",
    pricing_activePlan: "Aktiv plan",
    pricing_calculating: "Beräknar...",
    pricing_processing: "Behandlar...",
    pricing_changePlan: "Byt plan",
    pricing_choose: "Välj",
    pricing_currentSubStatus: "Din prenumerationsstatus",
    pricing_hideHistory: "Dölj historik",
    pricing_viewHistory: "Visa historik",
    pricing_planLabel: "Plan:",
    pricing_statusLabel: "Status:",
    pricing_expiresLabel: "Upphör:",
    pricing_recentBillingHistory: "Senaste faktureringar",
    pricing_noBillingHistory: "Ingen faktureringshistorik ännu.",
    pricing_confirmSubChange: "Bekräfta prenumerationsändringen",
    pricing_currentPlanLabel: "Nuvarande plan:",
    pricing_newPlanLabel: "Ny plan:",
    pricing_daysUsed: "Använda dagar:",
    pricing_of: "av",
    pricing_daysRemaining: "Återstående dagar:",
    pricing_proratedRefund: "Proportionerlig återbetalning:",
    pricing_noCharge: "Ingen avgift",
    pricing_netCharge: "Att betala",
    pricing_descriptionLabel: "Beskrivning:",
    pricing_cancel: "Avbryt",
    pricing_confirmChange: "Bekräfta ändring",
    subMgmt_title: "Hantera prenumeration",
    subMgmt_currentSubscription: "Din prenumeration",
    subMgmt_monthly: "Månadsvis",
    subMgmt_yearly: "Årsvis",
    subMgmt_statusLabel: "Status:",
    subMgmt_expiresLabel: "Upphör:",
    subMgmt_priceLabel: "Pris:",
    subMgmt_choosePlan: "Välj din plan",
    subMgmt_plusPlans: "Plus-planer",
    subMgmt_currentPlan: "Nuvarande plan",
    subMgmt_save: "Spara",
    subMgmt_month: "månad",
    subMgmt_select: "Välj",
    subMgmt_footer: "Alla planer ger tillgång till vårt nätverk av laddstationer och funktionerna för att hitta restauranger. Du kan avsluta eller ändra din plan när som helst.",
    tierChange_upgrade: "Uppgradera",
    tierChange_downgrade: "Nedgradera",
    tierChange_subscription: "prenumeration",
    tierChange_importantNotice: "Viktigt att veta",
    tierChange_downgradeWarning: "Om du nedgraderar din prenumeration får du tillgång till färre funktioner. Vissa funktioner kan sluta fungera direkt efter ändringen.",
    tierChange_aboutToUpgrade: "Du är på väg att uppgradera från:",
    tierChange_aboutToDowngrade: "Du är på väg att nedgradera från:",
    tierChange_month: "månad",
    tierChange_monthlyPriceChange: "Förändring av månadspriset:",
    tierChange_upgradeBillingInfo: "Din prenumeration uppgraderas direkt. Du betalar mellanskillnaden för den återstående delen av innevarande faktureringsperiod.",
    tierChange_downgradeBillingInfo: "Din prenumeration nedgraderas direkt. Eventuell oanvänd tid tillgodoräknas på din nästa faktura.",
    tierChange_cancel: "Avbryt",
    tierChange_processing: "Behandlar...",
    tierChange_confirm: "Bekräfta",
    // Navigation
    login: 'Logga in',
    register: 'Skapa konto',
    logout: 'Logga ut',
    settings: 'Inställningar',
    about: 'Om',
    pricing: 'Priser',
    faq: 'FAQ',
    contact: 'Kontakt',

    // Hero Section
    heroTitle: 'Hitta den bästa måltiden nära laddstationen',
    heroSubtitle: 'Upptäck topprankade restauranger inom gångavstånd från ditt laddstopp. Where2 hjälper dig att planera måltiden redan vid laddstationen och visar de bästa ställena att äta på i närheten – filtrerade efter dina önskemål och recensioner – så att du kan njuta av god mat medan bilen laddar.',
    getStarted: 'Nu kör vi!',
    learnMore: 'Visa hur det fungerar',

    // Features
    smartLocationSearch: 'Sök laddare',
    smartLocationDesc: 'Sök efter laddstationer på valfri plats och hitta bra restauranger i närheten.',
    searchChargers: 'Sök laddare',
    preOrderReady: 'Måltidsförslag',
    preOrderDesc: 'Med den här funktionen markerar du ett område på kartan dit du är på väg – så får du restaurangförslag som matchar dina önskemål.',
    restaurantDiscovery: 'Upptäck restauranger',
    restaurantDesc: 'Utforska nya matupplevelser med utförliga recensioner och foton – nära en laddstation längs din rutt.',

    // CTA Section
    ctaTitle: 'Redo att hitta ditt nästa favoritställe att äta på?',
    ctaSubtitle: 'Planera måltiden innan du är framme.',

    // Success Stories
    successStories: 'Det säger våra användare',
    successStoriesDesc: 'Se hur Where2 förändrar matupplevelsen på elbilsresan',
    shareYourStory: 'Dela din berättelse',

    // Main Button
    findNearbyRestaurants: 'Hitta restauranger',
    planMyMeal: '🚗 Planera min måltid',

    // Modals
    email: 'E-post',
    password: 'Lösenord',
    confirmPassword: 'Bekräfta lösenord',
    couponCode: 'Rabattkod (valfritt)',
    enterEmail: 'Ange din e-postadress',
    enterCoupon: 'Ange din rabattkod om du har en',
    dontHaveAccount: 'Har du inget konto? Registrera dig',

    // Settings
    yourLocationIcon: 'Din platsikon',
    priceRangePreferences: 'Prisönskemål',
    maxRestaurants: 'Max antal restauranger att visa',

    // Restaurant Popup
    nearestRestaurants: 'Närmaste restauranger:',
    open: 'Öppet',
    closed: 'Stängt',
    km: 'km',
    call: 'Ring',
    directions: 'Vägbeskrivning',
    map: 'Karta',
    showMap: 'Visa karta med restauranger',
    close: 'Stäng',

    // Disclaimer
    disclaimerTitle: 'Säkerhetsinformation',
    disclaimerText: 'Tänk på att den här funktionen inte får användas av föraren när fordonet är i rörelse. Den ska användas av en passagerare eller när bilen står stilla. Appen får bara användas under säkra förhållanden, och vi tar inget ansvar om dessa villkor eller andra säkerhetsföreskrifter inte följs.',
    iUnderstand: 'Jag förstår – fortsätt',
    doNotShowAgain: 'Visa inte den här varningen igen',
    importantSafetyNotice: 'Viktig säkerhetsinformation',
    safeUsageGuidelines: 'Riktlinjer för säker användning:',
    onlyUseWhenParked: 'Använd bara när du står parkerad: se till att fordonet står helt stilla och är parkerat',
    passengerUse: 'Låt passageraren sköta appen under körning',
    safeEnvironment: 'Säker miljö: använd bara på säkra och välbelysta platser',
    followTrafficLaws: 'Följ trafikreglerna: respektera alltid lokala trafikregler',
    stayAlert: 'Var uppmärksam: håll koll på din omgivning',
    cancel: 'Avbryt',
    disclaimerAcknowledgment: 'Genom att fortsätta intygar du att du har läst och förstått den här säkerhetsinformationen och att du använder appen på ett ansvarsfullt sätt.',
    settingsNote: 'Du kan ändra den här inställningen senare i dina kontoinställningar.',
    browserNote: 'Ditt val sparas i din webbläsare.',

    // Learn More
    learnMoreTitle: 'Om Where2',
    learnMoreText: 'Numera går laddningen betydligt snabbare, och för att få ut mesta möjliga av resan lönar det sig att vara förberedd på var man ska äta när man kommer fram till en laddstation. Med Where2 flyttar du restaurangvalet framåt och kan bestämma vilket ställe som passar er bäst redan innan du är framme. På så sätt planerar du den perfekta måltiden i förväg. Framöver kommer du dessutom att kunna lägga till restaurangönskemål och liknande för att hitta de laddstationer som passar dig allra bäst.',

    // Contact
    contactTitle: 'Kontakta oss',
    contactDesc: 'Har du en fråga eller feedback? Vi hör gärna från dig!',
    name: 'Namn',
    subject: 'Ämne',
    message: 'Meddelande',
    sendMessage: 'Skicka meddelande',
    enterName: 'Ange ditt namn',
    enterSubject: 'Ange ett ämne',
    enterMessage: 'Skriv ditt meddelande',
    messageSent: 'Meddelandet är skickat!',
    messageError: 'Det gick inte att skicka meddelandet. Försök igen.',
    sending: 'Skickar...',

    // Auth Errors
    emailPasswordRequired: 'E-post och lösenord krävs',
    invalidEmail: 'Ange en giltig e-postadress',
    passwordLength: 'Lösenordet måste vara mellan 6 och 128 tecken',
    passwordsDontMatch: 'Lösenorden stämmer inte överens',
    userExists: 'Användaren finns redan',
    invalidCredentials: 'Fel e-postadress eller lösenord',

    // Trial
    trialActive: 'Provperiod aktiv',
    trialExpired: 'Provperioden har löpt ut',
    trialDaysLeft: 'dagar kvar',
    trialHoursLeft: 'timmar kvar',

    // Subscription
    subscription: 'Prenumeration',
    free: 'Gratis',
    premium: 'Plus',
    expired: 'Utgången',
    active: 'Aktiv',

    // General
    loading: 'Laddar...',
    error: 'Fel',
    success: 'Klart!',
    save: 'Spara',
    ok: 'OK',
    yes: 'Ja',
    no: 'Nej',
  },
  es: {
    legend: 'Leyenda',
    // --- i18n additions ---
    app_successfullyUpgradedTo: "Actualizado correctamente a",
    app_successfullyDowngradedTo: "Cambiado correctamente a",
    app_failedToChangeSubscription: "No se pudo cambiar la suscripción",
    app_unknownError: "Error desconocido",
    app_reportThankYou: "Gracias por tu informe. Lo revisaremos en breve.",
    app_reportAlreadyReported: "Este lugar ya ha sido reportado. ¡Gracias por tu interés!",
    app_reportError: "Se produjo un error al enviar tu informe. Inténtalo de nuevo.",
    app_away: "de distancia",
    app_hoursNotAvailable: "Horario no disponible",
    app_website: "Sitio web",
    app_drive: "Conducir",
    app_geoUnableRetrieve: "No se pudo obtener tu ubicación.",
    app_geoPermissionDenied: "Acceso a la ubicación denegado. Activa los permisos de ubicación en tu navegador.",
    app_geoUnavailable: "Información de ubicación no disponible. Inténtalo de nuevo.",
    app_geoTimeout: "La solicitud de ubicación expiró. Inténtalo de nuevo.",
    app_geoErrorLabel: "Error de ubicación",
    app_geoErrorTitle: "Error de ubicación",
    app_geoDefaultLocationNote: "Se usa la zona de Uppsala como ubicación predeterminada. Activa los permisos de ubicación para una posición precisa.",
    app_geoFailedGetLocation: "No se pudo obtener tu ubicación. Inténtalo de nuevo.",
    app_geoNotSupported: "Tu navegador no admite la geolocalización.",
    app_tryAgainFewMinutes: "Inténtalo de nuevo en unos minutos.",
    app_errorLabel: "Error",
    app_aboutWhere2: "Acerca de Where2",
    app_aboutText: "El equipo detrás de Where2 lleva más de 10 años siendo propietario de vehículos eléctricos y ha vivido en primera persona la dificultad de encontrar buenas opciones de comida mientras se carga en carretera. Con demasiada frecuencia, los conductores llegan a un supercargador y no ven ningún restaurante desde el aparcamiento, o acaban en lugares que no cumplen las expectativas. Where2 se creó para resolver este problema: ayudamos a los conductores de vehículos eléctricos a descubrir los mejores restaurantes cercanos con reseñas reales, fotos e indicaciones a pie, para que aprovechen al máximo sus paradas de carga y disfruten del viaje.",
    app_welcomeBack: "Bienvenido de nuevo",
    app_readyToFindFood: "¿Listo para encontrar buena comida mientras cargas?",
    app_welcomeBackReady: "¡Bienvenido de nuevo! ¿Listo para encontrar buenos restaurantes mientras cargas?",
    app_plusFeatureUpgrade: "Función Plus: haz clic para actualizar",
    app_getSuggestions: "Obtener sugerencias",
    app_backToSuperchargerView: "Volver a la vista de supercargadores",
    app_closeMap: "Cerrar mapa",
    app_loadingMap: "Cargando mapa...",
    app_loadingChargers: "Cargando cargadores...",
    app_findingSuperchargers: "Buscando supercargadores en tu zona",
    app_fetchingStations: "Obteniendo estaciones de carga...",
    app_slowConnectionNote: "Esto puede tardar un momento con conexiones más lentas",
    app_zoomIn: "Acercar (centrado en tu ubicación)",
    app_zoomOut: "Alejar (centrado en tu ubicación)",
    app_chooseYourPreference: "Elige tu preferencia",
    app_prefNormal: "Preferencias normales",
    app_prefNicer: "Un poco mejor",
    app_prefCheaper: "Un poco más barato",
    app_searching: "Buscando...",
    app_startSearch: "Iniciar búsqueda",
    app_blueCircleNote: "Buscaremos restaurantes cerca de los cargadores dentro del círculo azul",
    app_found: "Se encontraron",
    app_restaurantsWord: "restaurantes",
    app_higherQuality: "(mayor calidad)",
    app_budgetFriendly: "(económico)",
    app_unknownCharger: "Cargador desconocido",
    app_seeAllRestaurants: "Ver todos los restaurantes",
    app_and: "y",
    app_moreRestaurants: "restaurantes más...",
    app_noRestaurantsInArea: "No se encontraron restaurantes en la zona seleccionada. Prueba a alejar el zoom o mover el mapa.",
    app_loading: "Cargando...",
    app_close: "Cerrar",
    app_learnTitle: "El futuro de los viajes en vehículo eléctrico",
    app_learnRealityTitle: "La nueva realidad de la carga de vehículos eléctricos",
    app_learnRealityText: "Hoy en día el tiempo de carga se ha reducido bastante y, para aprovechar al máximo un viaje, a veces conviene estar bien preparado para comer al llegar a un punto de carga. Esta aplicación te permite adelantar la elección del restaurante para poder decidir cuál conviene más a los viajeros incluso antes de llegar al cargador.",
    app_learnPreOrderTitle: "Planifica tu comida con antelación",
    app_learnPreOrderText: "De hecho, ¿por qué no decidir el restaurante adecuado antes incluso de llegar? Esto transforma tu parada de carga de un tiempo de espera en una parte fluida de tu viaje. Se acabó correr para encontrar comida o conformarse con lo que haya: puedes planificar tu comida perfecta con antelación.",
    app_learnHowTitle: "Cómo funciona Where2",
    app_learnStep1Title: "Encuentra estaciones de carga:",
    app_learnStep1Text: "Usa tu ubicación o busca estaciones de carga a lo largo de tu ruta. Where2 es compatible con Tesla Superchargers, Ionity, OKQ8 y otras redes.",
    app_learnStep2Title: "Descubre restaurantes:",
    app_learnStep2Text: "Consulta todos los restaurantes cercanos con información en tiempo real, incluidas valoraciones, fotos, horarios e indicaciones a pie desde el cargador.",
    app_learnStep3Title: "Planifica tu parada:",
    app_learnStep3Text: "Compara opciones, comprueba distancias e incluso obtén indicaciones a pie para aprovechar al máximo tu tiempo de carga.",
    app_learnVisionTitle: "La visión de futuro",
    app_learnVisionText: "En el futuro, Where2 evolucionará para incluir preferencias de restaurantes, requisitos dietéticos e incluso la integración con los sistemas de pedidos de los restaurantes. Imagina poder establecer tus preferencias de comida y que Where2 te sugiera las mejores paradas de carga según tus intereses culinarios. La aplicación aprenderá tus preferencias con el tiempo, haciendo cada viaje más personalizado y agradable.",
    app_learnBenefitsTitle: "Ventajas clave:",
    app_benefitTimeTitle: "Eficiencia de tiempo:",
    app_benefitTimeText: "Planifica tu comida antes de llegar, no después",
    app_benefitChoicesTitle: "Mejores opciones:",
    app_benefitChoicesText: "Consulta todas las opciones antes de llegar",
    app_benefitStressTitle: "Menos estrés:",
    app_benefitStressText: "Se acabó correr para encontrar comida",
    app_benefitJourneyTitle: "Mejora del viaje:",
    app_benefitJourneyText: "Convierte las paradas de carga en descansos agradables",
    app_benefitFutureTitle: "Preparado para el futuro:",
    app_benefitFutureText: "Plataforma para preferencias de restaurantes y descubrimiento",
    login_title: "Iniciar sesión",
    login_emailLabel: "Correo electrónico",
    login_emailPlaceholder: "Introduce tu dirección de correo electrónico",
    login_passwordLabel: "Contraseña",
    login_submit: "Iniciar sesión",
    login_cancel: "Cancelar",
    login_switchToRegister: "¿No tienes una cuenta? Regístrate",
    register_title: "Registrarse",
    register_trialTitle: "¡Prueba Plus gratuita!",
    register_trialTextBefore: "Obtén",
    register_trialTextAfter: "de acceso premium con todas las funciones desbloqueadas. Cuando finalice tu prueba, tu cuenta se convertirá en una cuenta gratuita limitada. Puedes actualizar a una suscripción de pago en cualquier momento para conservar las funciones premium.",
    register_unitMinutes: "minutos",
    register_unitHours: "horas",
    register_unitDays: "días",
    register_emailLabel: "Correo electrónico",
    register_emailPlaceholder: "Introduce tu dirección de correo electrónico",
    register_displayNameLabel: "Nombre para mostrar",
    register_displayNamePlaceholder: "Introduce tu nombre para mostrar",
    register_passwordLabel: "Contraseña",
    register_confirmPasswordLabel: "Confirmar contraseña",
    register_couponLabel: "Código de cupón (opcional)",
    register_couponPlaceholder: "Introduce el código de cupón si tienes uno",
    register_submit: "Registrarse",
    register_cancel: "Cancelar",
    register_switchToLogin: "¿Ya tienes una cuenta? Inicia sesión",
    verify_errorNoToken: "No se proporcionó ningún token de verificación",
    verify_successDefault: "¡Correo electrónico verificado correctamente!",
    verify_errorFailed: "La verificación falló",
    verify_errorNetwork: "Error de red. Inténtalo de nuevo.",
    verify_loadingTitle: "Verificando correo electrónico",
    verify_loadingText: "Espera mientras verificamos tu dirección de correo electrónico...",
    verify_successTitle: "¡Correo electrónico verificado!",
    verify_continue: "Continuar a Where2",
    verify_failedTitle: "La verificación falló",
    verify_goHome: "Ir a la página de inicio",
    verify_registerAgain: "Registrarse de nuevo",
    faq_close: "Cerrar",
    faq_title: "Preguntas frecuentes",
    faq_q1Question: "¿De qué va todo esto?",
    faq_q1Answer: "Solo una forma de estar algo más preparado para tu parada de carga",
    faq_q2Question: "¡No lo entiendo! ¿Qué hago?",
    faq_q2Answer: "¡Sin problema! En la página de inicio, haz clic en 'Muéstrame cómo funciona' para ver una guía rápida, o simplemente pulsa 'Buscar restaurantes' para ver los cargadores cercanos y los restaurantes a poca distancia a pie de cada uno.",
    faq_q3Question: "No lo entiendo. ¡No veo ningún restaurante!",
    faq_q3Answer: "Por supuesto, podría tratarse de uno de esos molestos errores, pero comprueba también si has configurado los ajustes como quieres. Aquí puedes establecer tus preferencias de restaurante y cuánto estás dispuesto a caminar mientras cargas. También puedes seleccionar niveles de precio y otras opciones de filtrado que te ayudan a encontrar la mejor opción para ti.",
    faq_q4Question: "¿Por qué no es gratis? ¡Debería ser un servicio!",
    faq_q4Answer: "Lamentablemente, la fuente de datos que utilizamos tampoco es gratuita, lo que significa que el uso de la aplicación conlleva a veces un coste elevado según el volumen. No obstante, intentamos mantener el precio de las distintas opciones al mínimo para cubrir los gastos incluso con una carga alta.",
    faq_q5Question: "¿Por qué hay diferentes niveles de suscripción?",
    faq_q5AnswerPart1: "Lo hacemos sencillo: un nivel ",
    faq_freeTier: "Free",
    faq_q5AnswerPart2: " para la búsqueda básica de restaurantes, y un único nivel de pago ",
    faq_plusTier: "Plus",
    faq_q5AnswerPart3: " que lo desbloquea todo: fotos y reseñas de restaurantes, filtrado avanzado, optimización de rutas, la función \"sugerir una comida\" y tipos de búsqueda adicionales (hoteles, compras).",
    faq_q6Question: "¿No es esto en realidad un problema de lujo que no necesita solución?",
    faq_q6Answer: "Sí, por supuesto, pero esto es algo que los creadores del sitio siempre han considerado un ladrón de tiempo y una molestia, ya que los puntos de carga son muy distintos y tienen opciones muy diferentes, ¡así que estar preparado nunca está de más!",
    faq_q7Question: "¿Cómo se supone que debo usar esto en el coche?",
    faq_q7Answer: "Estamos trabajando en una versión para CarPlay. Esperamos tenerla disponible en breve. Mientras tanto, sugerimos usar el móvil o quizá una tableta si está disponible. Algunos vehículos eléctricos tienen navegador web integrado y ahí debería funcionar bien. ¡Recuerda no usar la aplicación mientras conduces!",
    faq_q8Question: "También quiero encontrar hoteles y tiendas cerca del cargador. ¿No podéis mostrar eso también?",
    faq_q8Answer: "También estamos trabajando en estas opciones. Consulta la sección de novedades para ver actualizaciones al respecto.",
    faq_q9Question: "¿No tenéis alguna buena integración de IA en la que pueda decirle a la aplicación lo que quiero?",
    faq_q9Answer: "Hemos probado algunas alternativas al respecto, pero descubrimos que los resultados son, en el mejor de los casos, poco fiables y, en su mayoría, absurdos e imprecisos. Por eso hemos optado por la forma tradicional de intentar ayudarte a encontrar un buen restaurante cerca de un cargador.",
    faq_q10Question: "¿De dónde provienen las reseñas?",
    faq_q10Answer: "Usamos Google Places como fuente de reseñas. Es posible que cambiemos esto en el futuro. Quizá incluso empecemos a colaborar con la base de usuarios de la aplicación para las reseñas, pero por ahora los usuarios de Google son los jueces.",
    faq_q11Question: "¿Qué hago si encuentro algo que no funciona?",
    faq_q11AnswerPart1: "Por favor, ",
    faq_contactUsHere: "contáctanos aquí",
    faq_q11AnswerPart2: " si tienes algún problema. ¡Estamos aquí para ayudar!",
    faq_q12Question: "¿Qué hago si quiero una nueva función?",
    faq_q12AnswerPart1: "Lo mismo. Por favor, ",
    faq_contactUs: "contáctanos",
    faq_q12AnswerPart2: " y veremos qué podemos hacer!",
    faq_q13Question: "¿Cómo funciona Where2?",
    faq_q13Answer: "Where2 ayuda a los conductores de vehículos eléctricos a encontrar restaurantes cerca de las estaciones de carga. Simplemente haz clic en \"Buscar un restaurante\" para encontrar supercargadores cercanos y ver opciones de restaurantes a poca distancia a pie.",
    faq_q14Question: "¿Qué redes de carga son compatibles?",
    faq_q14Answer: "Actualmente admitimos Tesla Superchargers, Ionity y OKQ8. Puedes seleccionar qué redes mostrar en tus ajustes.",
    faq_q15Question: "¿Hasta qué distancia puedo buscar restaurantes?",
    faq_q15Answer: "Puedes ajustar el radio de búsqueda de restaurantes de 0,2 km a 1,6 km en tus ajustes. El valor predeterminado es 0,6 km para una distancia a pie óptima.",
    faq_q16Question: "¿Hasta qué distancia puedo buscar supercargadores?",
    faq_q16Answer: "Puedes ajustar el radio de búsqueda de supercargadores de 20 km a 400 km en tus ajustes. El valor predeterminado es 100 km.",
    faq_q17Question: "¿Tengo que pagar para usar Where2?",
    faq_q17AnswerPart1: "Where2 tiene un nivel ",
    faq_q17AnswerPart2: " más una suscripción de pago: ",
    faq_q17AnswerPart3: " (3 USD al mes o 30 USD al año) desbloquea fotos y reseñas de restaurantes, filtrado avanzado, optimización de rutas, la función \"sugerir una comida\" y la búsqueda de hoteles y tiendas.",
    faq_q18Question: "¿Qué precisión tiene la información de los restaurantes?",
    faq_q18Answer: "Usamos la API de Google Places para proporcionar datos de restaurantes en tiempo real, incluidas valoraciones, fotos, horarios de apertura e información de contacto.",
    faq_q19Question: "¿Puedo obtener indicaciones a pie hasta los restaurantes?",
    faq_q19Answer: "¡Sí! Haz clic en el enlace \"🚶 Ruta a pie\" junto a cualquier restaurante para obtener indicaciones a pie en Google Maps desde el supercargador hasta el restaurante.",
    faq_q20Question: "¿Se almacenan mis datos de ubicación?",
    faq_q20Answer: "No, no almacenamos tus datos de ubicación. Tu ubicación solo se usa temporalmente para encontrar supercargadores y restaurantes cercanos.",
    about_close: "Cerrar",
    about_title: "Acerca de Where2",
    about_body: "El equipo detrás de Where2 ha sido propietario de vehículos eléctricos durante más de 10 años, experimentando de primera mano las dificultades de encontrar buenas opciones de comida mientras se carga en la carretera. Con demasiada frecuencia, los conductores llegan a un supercargador y no ven ningún restaurante desde el aparcamiento, o acaban en lugares que no cumplen sus expectativas. Where2 se creó para resolver este problema: ayudar a los conductores de vehículos eléctricos a descubrir los mejores restaurantes cercanos con reseñas reales, fotos e indicaciones a pie, para que puedan aprovechar al máximo sus paradas de carga y disfrutar de su viaje.",
    learnMore_close: "Cerrar",
    learnMore_title: "El futuro de los viajes en vehículo eléctrico",
    learnMore_section1Title: "La nueva realidad de la carga de vehículos eléctricos",
    learnMore_section1Body: "Hoy en día el tiempo de carga se ha reducido bastante y, para aprovechar al máximo un viaje, a veces conviene estar bien preparado para comer al llegar a un punto de carga. Esta aplicación te permite adelantar la elección del restaurante para poder decidir cuál conviene más a los viajeros incluso antes de llegar al cargador.",
    learnMore_section2Title: "Planifica tu comida con antelación",
    learnMore_section2Body: "De hecho, ¿por qué no decidir el restaurante adecuado antes incluso de llegar? Esto transforma tu parada de carga de un tiempo de espera en una parte fluida de tu viaje. Se acabó tener que buscar comida con prisas o conformarse con lo que haya: puedes planificar tu comida perfecta con antelación.",
    learnMore_section3Title: "Cómo funciona Where2",
    learnMore_step1Label: "Encuentra estaciones de carga:",
    learnMore_step1Body: " Usa tu ubicación o busca estaciones de carga a lo largo de tu ruta. Where2 admite Tesla Superchargers, Ionity, OKQ8 y otras redes.",
    learnMore_step2Label: "Descubre restaurantes:",
    learnMore_step2Body: " Ve todos los restaurantes cercanos con información en tiempo real, incluidas valoraciones, fotos, horarios de apertura e indicaciones a pie desde el cargador.",
    learnMore_step3Label: "Planifica tu parada:",
    learnMore_step3Body: " Compara opciones, comprueba distancias e incluso obtén indicaciones a pie para aprovechar al máximo tu tiempo de carga.",
    learnMore_benefitsTitle: "Ventajas clave:",
    learnMore_benefit1Label: "Eficiencia de tiempo:",
    learnMore_benefit1Body: " Planifica tu comida antes de llegar, no después",
    learnMore_benefit2Label: "Mejores opciones:",
    learnMore_benefit2Body: " Ve todas las opciones antes de llegar",
    learnMore_benefit3Label: "Reducción del estrés:",
    learnMore_benefit3Body: " Se acabó buscar comida con prisas",
    learnMore_benefit4Label: "Mejora del viaje:",
    learnMore_benefit4Body: " Convierte las paradas de carga en pausas agradables",
    learnMore_benefit5Label: "Preparado para el futuro:",
    learnMore_benefit5Body: " Plataforma para preferencias de restaurantes y descubrimiento",
    contact_close: "Cerrar",
    contact_title: "Contáctanos",
    contact_subtitle: "¿Tienes una pregunta? ¡Nos encantaría saber de ti!",
    contact_nameLabel: "Nombre *",
    contact_namePlaceholder: "Tu nombre",
    contact_emailLabel: "Correo electrónico *",
    contact_emailPlaceholder: "your.email@example.com",
    contact_subjectLabel: "Asunto *",
    contact_subjectPlaceholder: "¿Sobre qué es tu pregunta?",
    contact_messageLabel: "Mensaje *",
    contact_messagePlaceholder: "Cuéntanos más sobre tu pregunta...",
    contact_successMessage: "¡Gracias! Tu mensaje se ha enviado correctamente. Nos pondremos en contacto contigo pronto.",
    contact_errorMessage: "Lo sentimos, hubo un error al enviar tu mensaje. Inténtalo de nuevo.",
    contact_cancel: "Cancelar",
    contact_sending: "Enviando...",
    contact_sendMessage: "Enviar mensaje",
    cookie_title: "Aviso sobre cookies",
    cookie_body1: "Utilizamos cookies para mantener tu sesión autenticada mientras usas nuestro servicio. Estas cookies son esenciales para que el sitio web funcione correctamente y te ofrezca una experiencia segura.",
    cookie_importantLabel: "Importante:",
    cookie_importantText: " No utilizamos cookies para rastrearte entre sitios web ni para ninguna actividad de rastreo entre sitios. Nuestras cookies se limitan estrictamente a la gestión de sesiones y la autenticación en nuestra plataforma.",
    cookie_storeLabel: "Qué almacenamos:",
    cookie_storeText: " Solo tokens de sesión para la autenticación de inicio de sesión",
    cookie_dontLabel: "Qué no hacemos:",
    cookie_dontText: " Rastreo entre sitios, publicidad o intercambio de datos",
    cookie_acceptCookies: "Aceptar cookies",
    cookie_decline: "Rechazar",
    cookie_rememberNote: "Recordaremos tu elección para futuras visitas",
    disclaimer_close: "Cerrar",
    disclaimer_title: "Aviso de seguridad",
    disclaimer_safetyBody: "Ten en cuenta que esta función no puede ser utilizada por el conductor cuando el vehículo no está estacionado. Debe ser utilizada por un pasajero o con el vehículo detenido. El uso de esta aplicación/sitio web está totalmente restringido a condiciones seguras y no se aceptará ninguna responsabilidad si no se cumplen estas restricciones o cualquier otra condición de uso insegura.",
    nav_manageSubscription: "Haz clic para gestionar tu suscripción",
    nav_mapCache: "Caché de mapa",
    nav_tiles: "teselas",
    nav_saved: "ahorrado",
    nav_signOut: "Cerrar sesión",
    mainBtn_plusFeatureUpgrade: "Función Plus: haz clic para actualizar",
    legend_stalls: "puestos",
    legend_naStalls: "puestos N/D",
    legend_fromYourLocation: "desde tu ubicación",
    legend_drive: "en coche",
    legend_arriveAt: "Llegada a las",
    legend_favoriteRestaurant: "Restaurante favorito",
    map_favoriteChargerTooltip: "Un restaurante favorito está cerca",
    legend_hoursNotAvailable: "Horario no disponible",
    legend_priceBudget: "Económico",
    legend_priceModerate: "Moderado",
    legend_priceExpensive: "Caro",
    legend_priceVeryExpensive: "Muy caro",
    legend_reviews: "reseñas",
    legend_web: "Web",
    legend_call: "Llamar",
    legend_directions: "Cómo llegar",
    legend_reportTooltip: "Informa si esto no es realmente un restaurante",
    legend_notARestaurant: "¡No es un restaurante!",
    floatLegend_stalls: "puestos",
    floatLegend_naStalls: "puestos N/D",
    floatLegend_openInGoogleMaps: "Abrir en Google Maps",
    floatLegend_maps: "Mapas",
    floatLegend_closeLegend: "Cerrar leyenda",
    floatLegend_selectedCharger: "Cargador seleccionado",
    popup_closePopup: "Cerrar ventana",
    popup_supercharger: "Supercharger",
    popup_chargingStation: "Estación de carga",
    popup_restaurantsFoundNearby: "restaurantes encontrados cerca",
    popup_loginToSeeDetails: "Inicia sesión para ver los detalles del restaurante",
    popup_loadingRestaurants: "Cargando restaurantes...",
    restCard_away: "de distancia",
    restCard_hoursNotAvailable: "Horario no disponible",
    restCard_website: "Sitio web",
    restCard_call: "Llamar",
    restCard_directions: "Cómo llegar",
    restCard_teslaNav: "Tesla Nav",
    proxActions_wait1h: "Esperar 1 h",
    proxActions_wait2h: "Esperar 2 h",
    proxActions_turnOffAll: "Desactivar todas las notificaciones",
    proxNotif_away: "de distancia",
    proxNotif_greatRestaurantsNearby: "buenos restaurantes cerca: ¡perfectos para una pausa para comer!",
    proxNotif_topRestaurants: "Mejores restaurantes:",
    proxNotif_clicked: "¡Clic!",
    proxNotif_showMe: "Mostrar",
    proxNotif_close: "Cerrar",
    chargerSearch_min3chars: "Introduce al menos 3 caracteres para buscar",
    chargerSearch_searchFailed: "La búsqueda falló",
    chargerSearch_locationSearchFailed: "La búsqueda de ubicación falló",
    chargerSearch_chargerSearchFailed: "La búsqueda de cargadores falló",
    chargerSearch_unknown: "Desconocido",
    chargerSearch_title: "Buscar cargadores",
    chargerSearch_placeholder: "Busca cargadores por ciudad o ubicación (p. ej., Stockholm, Sollentuna)...",
    chargerSearch_typeMin3: "Escribe al menos 3 caracteres para buscar",
    chargerSearch_searchingAround: "Buscando cerca de:",
    chargerSearch_noChargersFound: "No se encontraron cargadores para",
    chargerSearch_tryDifferent: "Prueba a buscar otra ciudad o ubicación",
    chargerSearch_na: "N/D",
    chargerSearch_chargingPoints: "puntos de carga",
    chargerSearch_more: "más",
    chargerSearch_selectCharger: "Seleccionar cargador",
    chargerSearch_findPerfectStop: "Encuentra tu parada de carga perfecta",
    chargerSearch_emptyStateDesc: "Busca cargadores por ciudad o ubicación para encontrar los mejores lugares para cargar y comer.",
    report_title: "Reportar no restaurante",
    report_aboutGoogleData: "Acerca de los datos de Google Places",
    report_para1: "La API de Google a veces clasifica negocios que no son restaurantes (como gasolineras, tiendas o servicios) como restaurantes. Esto puede ocurrir cuando estos negocios sirven comida como servicio secundario.",
    report_para2: "Si crees que este lugar no es principalmente un restaurante, háznoslo saber para que podamos mejorar nuestras recomendaciones.",
    report_reasonLabel: "¿Por qué no es un restaurante? (Opcional)",
    report_reasonPlaceholder: "p. ej., 'Es una gasolinera que solo vende aperitivos' o 'Es un supermercado, no un restaurante'",
    report_cancel: "Cancelar",
    report_submitting: "Enviando...",
    report_submit: "Enviar informe",
    mealPlan_south: "SUR",
    mealPlan_north: "NORTE",
    mealPlan_latitudeStable: "LATITUD ESTABLE",
    mealPlan_west: "OESTE",
    mealPlan_east: "ESTE",
    mealPlan_title: "¡PLAN DE COMIDAS GENERADO!",
    mealPlan_close: "Cerrar",
    mealPlan_locationHistory: "HISTORIAL DE UBICACIÓN",
    mealPlan_travelDirection: "DIRECCIÓN DE VIAJE",
    mealPlan_vector: "Vector:",
    mealPlan_direction: "Dirección:",
    mealPlan_superchargersInDirection: "SUPERCHARGERS EN LA DIRECCIÓN",
    mealPlan_foundInDirection: "Encontrados en la dirección de viaje:",
    mealPlan_selectedForPlan: "Seleccionados para el plan de comidas:",
    mealPlan_top: "Los",
    mealPlan_byDistance: "por distancia",
    mealPlan_mealPlanHeading: "PLAN DE COMIDAS",
    mealPlan_away: "de distancia",
    mealPlan_minTravelTime: "min de viaje",
    mealPlan_fromCharger: "desde el cargador",
    mealPlan_hoursNotAvailable: "Horario no disponible",
    mealPlan_fromCache: "(desde caché)",
    mealPlan_noRestaurantsFound: "No se encontraron restaurantes",
    mealPlan_noRestaurantsDesc: "No se encontraron restaurantes en los superchargers seleccionados.",
    mealPlan_jensTestMode: "MODO DE PRUEBA JENS ACTIVO",
    mealPlan_simulatedTravel: "Viaje simulado hacia el sur: Stockholm → Sur",
    suggestMeal_waitForMap: "Espera a que el mapa se cargue por completo antes de buscar.",
    suggestMeal_noChargers: "No se encontraron estaciones de carga en la zona ni cerca. Prueba otra ubicación o aleja más el zoom.",
    suggestMeal_searchFailed: "La búsqueda falló. Inténtalo de nuevo.",
    suggestMeal_prefNormal: "Preferencias normales",
    suggestMeal_prefNicer: "Un poco mejor",
    suggestMeal_prefCheaper: "Un poco más barato",
    suggestMeal_descNormal: "Recomendaciones equilibradas según las valoraciones",
    suggestMeal_descNicer: "Cargadores y restaurantes premium con valoraciones de 4,0+",
    suggestMeal_descCheaper: "Opciones económicas con valoraciones de 3,5+",
    suggestMeal_title: "Selecciona una zona para sugerencias de comida",
    suggestMeal_close: "Cerrar",
    suggestMeal_fromYourLocation: "desde tu ubicación",
    suggestMeal_estimated: "(estimado)",
    suggestMeal_minDrive: "min en coche desde aquí",
    suggestMeal_arriveAt: "Llegada a las",
    suggestMeal_backToSearch: "Volver al área de búsqueda",
    suggestMeal_loadingMap: "Cargando mapa...",
    suggestMeal_searching: "Buscando...",
    suggestMeal_startSearch: "Iniciar búsqueda",
    suggestMeal_dragInstruction: "Arrastra el mapa para colocar el círculo azul sobre el área de búsqueda deseada y luego haz clic en Iniciar",
    miniMap_close: "Cerrar",
    miniMap_map: "Mapa:",
    miniMap_sendToTesla: "Enviar a la app de Tesla",
    miniMap_teslaNavigation: "Navegación Tesla",
    miniMap_nav: "Nav",
    miniMap_googleMapsNav: "Navegación de Google Maps",
    miniMap_maps: "Mapas",
    miniMap_drive: "Conducir",
    settings_title: "Ajustes",
    settings_subtitle: "Personaliza tu experiencia",
    settings_restaurantRadius: "Radio de búsqueda de restaurantes",
    settings_chargerRadius: "Radio de búsqueda de cargadores",
    settings_chargerNetworks: "Redes de carga",
    settings_unitSystem: "Sistema de unidades",
    settings_metric: "Métrico (km, °C)",
    settings_imperial: "Imperial (mi, °F)",
    settings_save: "Guardar ajustes",
    settings_cancel: "Cancelar",
    settings_profileSettings: "Ajustes de perfil",
    settings_emailAddress: "Correo electrónico",
    settings_emailPlaceholder: "Correo electrónico",
    settings_fullName: "Nombre completo",
    settings_fullNamePlaceholder: "Introduce tu nombre completo",
    settings_searchPreferences: "Preferencias de búsqueda",
    settings_current: "Actual",
    settings_evChargingNetworks: "Redes de carga para VE",
    settings_displayPreferences: "Preferencias de visualización",
    settings_metricUnits: "Unidades métricas",
    settings_usingMetric: "Usando kilómetros y Celsius",
    settings_usingImperial: "Usando millas y Fahrenheit",
    settings_currencyUSD: "Dólar estadounidense",
    settings_currencyEUR: "Euro",
    settings_currencySEK: "Corona sueca",
    settings_currencyDKK: "Corona danesa",
    settings_currencyNOK: "Corona noruega",
    settings_currencyCHF: "Franco suizo",
    settings_currency: "Moneda",
    settings_foodPreferences: "Preferencias de comida",
    settings_priceLevelsChains: "Niveles de precio y cadenas",
    settings_subscription: "Suscripción",
    settings_manageYourPlan: "Gestiona tu plan",
    settings_saving: "Guardando...",
    prefs_close: "Cerrar",
    prefs_title: "Preferencias de restaurantes",
    prefs_loading: "Cargando preferencias...",
    prefs_preferredChains: "Cadenas de restaurantes preferidas",
    prefs_loadingChains: "Cargando cadenas de restaurantes...",
    prefs_customKeywords: "Palabras clave personalizadas",
    prefs_keywordPlaceholder: "p. ej., hamburguesa, pizza, sushi...",
    prefs_add: "Añadir",
    prefs_priceLevels: "Niveles de precio",
    prefs_priceLevelsDesc: "Selecciona los rangos de precio que prefieres para los restaurantes",
    prefs_price1_label: "Económico",
    prefs_price1_desc: "Económico ($)",
    prefs_price2_label: "Moderado",
    prefs_price2_desc: "Moderado ($$)",
    prefs_price3_label: "Caro",
    prefs_price3_desc: "Caro ($$$)",
    prefs_price4_label: "Muy caro",
    prefs_price4_desc: "Muy caro ($$$$)",
    prefs_searchConfiguration: "Configuración de búsqueda",
    prefs_searchLevel_include_all_name: "Incluir todos los supercargadores (predeterminado)",
    prefs_searchLevel_include_all_desc: "Muestra todos los cargadores y resalta los restaurantes preferidos con un borde dorado",
    prefs_searchLevel_show_favorites_only_name: "Mostrar solo mis restaurantes favoritos",
    prefs_searchLevel_show_favorites_only_desc: "Muestra todos los cargadores pero solo los restaurantes que coinciden con tus preferencias",
    prefs_searchLevel_favorites_only_chargers_name: "Solo cargadores con restaurantes favoritos",
    prefs_searchLevel_favorites_only_chargers_desc: "Muestra solo los cargadores que tienen restaurantes cercanos que coinciden con tus preferencias",
    prefs_savedSuccess: "¡Preferencias guardadas correctamente!",
    prefs_saving: "Guardando...",
    prefs_savePreferences: "Guardar preferencias",
    prefs_cancel: "Cancelar",
    prefs_errorLoad: "No se pudieron cargar las preferencias",
    prefs_errorSave: "No se pudieron guardar las preferencias",
    prefs_unknownError: "Se produjo un error desconocido",
    restCount_foundSingular: "restaurante encontrado",
    restCount_foundPlural: "restaurantes encontrados",
    restCount_near: "cerca de",
    restCount_signupPrompt: "¡Regístrate para ver detalles de restaurantes, fotos, reseñas y más!",
    restCount_unlockFullDetails: "Desbloquear todos los detalles",
    restCount_freeToSignup: "Registro gratuito • Funciones Plus disponibles",
    restCount_plusFeaturesInclude: "Las funciones Plus incluyen:",
    restCount_featureRatings: "Valoraciones y reseñas de restaurantes",
    restCount_featurePrices: "Niveles de precio y tipos de cocina",
    restCount_featureNavigation: "Navegación e indicaciones",
    pricing_errorCalculating: "Error al calcular el cambio de suscripción: ",
    pricing_failedCalculate: "No se pudo calcular el cambio de suscripción",
    pricing_changeSuccess: "¡Suscripción cambiada correctamente!",
    pricing_errorChanging: "Error al cambiar la suscripción: ",
    pricing_failedChange: "No se pudo cambiar la suscripción",
    pricing_plusPlanTitle: "Plan Plus",
    pricing_plusPlanSubtitle: "Todo lo que necesitas para viajes en coche eléctrico",
    pricing_close: "Cerrar",
    pricing_choosePlan: "Elige tu plan",
    pricing_pricesShownIn: "Precios mostrados en",
    pricing_mostPopular: "Más popular",
    pricing_monthly: "mensual",
    pricing_yearly: "anual",
    pricing_save: "Ahorra",
    pricing_month: "mes",
    pricing_year: "año",
    pricing_monthBilledYearly: "mes con facturación anual",
    pricing_activePlan: "Plan activo",
    pricing_calculating: "Calculando...",
    pricing_processing: "Procesando...",
    pricing_changePlan: "Cambiar plan",
    pricing_choose: "Elegir",
    pricing_currentSubStatus: "Estado actual de la suscripción",
    pricing_hideHistory: "Ocultar historial",
    pricing_viewHistory: "Ver historial",
    pricing_planLabel: "Plan:",
    pricing_statusLabel: "Estado:",
    pricing_expiresLabel: "Caduca:",
    pricing_recentBillingHistory: "Historial de facturación reciente",
    pricing_noBillingHistory: "Aún no hay historial de facturación.",
    pricing_confirmSubChange: "Confirmar cambio de suscripción",
    pricing_currentPlanLabel: "Plan actual:",
    pricing_newPlanLabel: "Nuevo plan:",
    pricing_daysUsed: "Días usados:",
    pricing_of: "de",
    pricing_daysRemaining: "Días restantes:",
    pricing_proratedRefund: "Reembolso prorrateado:",
    pricing_noCharge: "Sin cargo",
    pricing_netCharge: "Cargo neto",
    pricing_descriptionLabel: "Descripción:",
    pricing_cancel: "Cancelar",
    pricing_confirmChange: "Confirmar cambio",
    subMgmt_title: "Gestión de suscripción",
    subMgmt_currentSubscription: "Suscripción actual",
    subMgmt_monthly: "Mensual",
    subMgmt_yearly: "Anual",
    subMgmt_statusLabel: "Estado:",
    subMgmt_expiresLabel: "Caduca:",
    subMgmt_priceLabel: "Precio:",
    subMgmt_choosePlan: "Elige tu plan",
    subMgmt_plusPlans: "Planes Plus",
    subMgmt_currentPlan: "Plan actual",
    subMgmt_save: "Ahorra",
    subMgmt_month: "mes",
    subMgmt_select: "Seleccionar",
    subMgmt_footer: "Todos los planes incluyen acceso a nuestra red de estaciones de carga para vehículos eléctricos y funciones para descubrir restaurantes. Cancela o cambia tu plan en cualquier momento.",
    tierChange_upgrade: "Mejorar",
    tierChange_downgrade: "Reducir",
    tierChange_subscription: "suscripción",
    tierChange_importantNotice: "Aviso importante",
    tierChange_downgradeWarning: "Reducir tu suscripción disminuirá las funciones disponibles. Algunas funciones pueden dejar de estar disponibles inmediatamente después del cambio.",
    tierChange_aboutToUpgrade: "Estás a punto de mejorar desde:",
    tierChange_aboutToDowngrade: "Estás a punto de reducir desde:",
    tierChange_month: "mes",
    tierChange_monthlyPriceChange: "Cambio de precio mensual:",
    tierChange_upgradeBillingInfo: "Tu suscripción se mejorará de inmediato. Se te cobrará la diferencia prorrateada del período de facturación actual.",
    tierChange_downgradeBillingInfo: "Tu suscripción se reducirá de inmediato. Cualquier parte no utilizada se acreditará en tu próximo ciclo de facturación.",
    tierChange_cancel: "Cancelar",
    tierChange_processing: "Procesando...",
    tierChange_confirm: "Confirmar",
    // Navigation
    login: 'Iniciar sesión',
    register: 'Registrarse',
    logout: 'Cerrar sesión',
    settings: 'Configuración',
    about: 'Acerca de',
    pricing: 'Precios',
    faq: 'FAQ',
    contact: 'Contacto',
    
    // Hero Section
    heroTitle: 'Encuentra la mejor comida cerca de tu cargador de EV',
    heroSubtitle: 'Descubre restaurantes mejor valorados a poca distancia de tu parada de carga. Where2 te ayuda a planificar tu descanso para almorzar en superchargers, mostrándote los mejores lugares para comer cerca—filtrados por tus preferencias y reseñas—para que puedas disfrutar de una buena comida mientras tu coche se carga.',
    getStarted: '¡Vamos!',
    learnMore: 'Muéstrame cómo funciona',
    
    // Features
    smartLocationSearch: 'Buscar cargadores',
    smartLocationDesc: 'Busca estaciones de carga por ubicación y encuentra excelentes restaurantes cercanos.',
    searchChargers: 'Buscar cargadores',
    preOrderReady: 'Sugerencias de comida',
    preOrderDesc: 'Usa esta función para seleccionar en el mapa la zona a la que te diriges y donde te gustaría recibir sugerencias de restaurantes según tus preferencias.',
    restaurantDiscovery: 'Descubrimiento de restaurantes',
    restaurantDesc: 'Explora nuevas experiencias gastronómicas con reseñas detalladas y fotos.',
    
    // CTA Section
    ctaTitle: '¿Listo para encontrar tu próximo lugar favorito para almorzar?',
    ctaSubtitle: 'Planifica tu comida antes de llegar.',
    
    // Success Stories
    successStories: 'Historias de éxito',
    successStoriesDesc: 'Ve cómo Where2 está transformando la experiencia gastronómica de EV',
    shareYourStory: 'Comparte tu historia',
    
    // Main Button
    findNearbyRestaurants: 'Buscar restaurantes',
    planMyMeal: '🚗 Planifica mi comida',
    
    // Modals
    email: 'Correo electrónico',
    password: 'Contraseña',
    confirmPassword: 'Confirmar contraseña',
    couponCode: 'Código de cupón (Opcional)',
    enterEmail: 'Ingresa tu dirección de correo electrónico',
    enterCoupon: 'Ingresa código de cupón si tienes uno',
    dontHaveAccount: '¿No tienes cuenta? Regístrate',
    
    // Settings
    yourLocationIcon: 'Tu icono de ubicación',
    priceRangePreferences: 'Preferencias de rango de precios',
    maxRestaurants: 'Máximo restaurantes a mostrar',
    
    // Restaurant Popup
    nearestRestaurants: 'Restaurantes más cercanos:',
    open: 'Abierto',
    closed: 'Cerrado',
    km: 'km',
    call: 'Llamar',
    directions: 'Direcciones',
    map: 'Mapa',
    showMap: 'Mostrar mapa con restaurantes',
    close: 'Cerrar',
    
    // Disclaimer
    disclaimerTitle: 'Descargo de responsabilidad de seguridad',
    disclaimerText: 'Tenga en cuenta que esta función no puede ser utilizada por el conductor cuando el vehículo no está estacionado. Debe ser utilizada por un pasajero o cuando el vehículo está detenido. El uso de esta aplicación web está completamente restringido a condiciones seguras y no se aceptará responsabilidad si no se cumplen estas restricciones o cualquier otra condición de uso inseguro.',
    iUnderstand: 'Entiendo - Continuar',
    doNotShowAgain: 'No mostrar este descargo de responsabilidad nuevamente',
    importantSafetyNotice: 'Aviso importante de seguridad',
    safeUsageGuidelines: 'Pautas de uso seguro:',
    onlyUseWhenParked: 'Usar solo cuando esté estacionado: Asegúrese de que su vehículo esté completamente detenido y estacionado',
    passengerUse: 'Uso de pasajeros: Deje que los pasajeros manejen la aplicación mientras conduce',
    safeEnvironment: 'Entorno seguro: Usar solo en áreas seguras y bien iluminadas',
    followTrafficLaws: 'Seguir las leyes de tránsito: Siempre cumplir con las regulaciones de tránsito locales',
    stayAlert: 'Mantenerse alerta: Mantener la conciencia de su entorno',
    cancel: 'Cancelar',
    disclaimerAcknowledgment: 'Al continuar, reconoce que ha leído y entendido este descargo de responsabilidad de seguridad y acepta usar la aplicación de manera responsable.',
    settingsNote: 'Puede cambiar esta configuración más tarde en la configuración de su cuenta.',
    browserNote: 'Esta preferencia se guardará en su navegador.',
    
    // Learn More
    learnMoreTitle: 'Acerca de Where2',
    learnMoreText: 'Hoy en día el tiempo de carga se ha reducido bastante y para aprovechar al máximo un viaje a veces es bueno estar bien preparado para comer al llegar a una ubicación de carga. Esta aplicación te permitirá mover la consideración del restaurante para que se pueda decidir cuál se adapta mejor a los viajeros incluso antes de llegar al cargador. De este modo puedes planificar tu comida perfecta con antelación... y luego más descripción técnica sobre cómo funciona la aplicación y que en el futuro será posible agregar preferencias de restaurantes y similares para encontrar los mejores cargadores',
    
    // Contact
    contactTitle: 'Contáctanos',
    contactDesc: '¿Tienes una pregunta o comentarios? ¡Nos encantaría saber de ti!',
    name: 'Nombre',
    subject: 'Asunto',
    message: 'Mensaje',
    sendMessage: 'Enviar mensaje',
    enterName: 'Ingresa tu nombre',
    enterSubject: 'Ingresa el asunto',
    enterMessage: 'Ingresa tu mensaje',
    messageSent: '¡Mensaje enviado exitosamente!',
    messageError: 'Error al enviar mensaje. Inténtalo de nuevo.',
    sending: 'Enviando...',
    
    // Auth Errors
    emailPasswordRequired: 'Se requiere correo electrónico y contraseña',
    invalidEmail: 'Por favor ingresa una dirección de correo electrónico válida',
    passwordLength: 'La contraseña debe tener entre 6 y 128 caracteres',
    passwordsDontMatch: 'Las contraseñas no coinciden',
    userExists: 'El usuario ya existe',
    invalidCredentials: 'Correo electrónico o contraseña inválidos',
    
    // Trial
    trialActive: 'Prueba activa',
    trialExpired: 'Prueba expirada',
    trialDaysLeft: 'días restantes',
    trialHoursLeft: 'horas restantes',
    
    // Subscription
    subscription: 'Suscripción',
    free: 'Gratis',
    premium: 'Plus',
    expired: 'Expirada',
    active: 'Activa',
    
    // General
    loading: 'Cargando...',
    error: 'Error',
    success: 'Éxito',
    save: 'Guardar',
    ok: 'OK',
    yes: 'Sí',
    no: 'No',
  },
  de: {
    legend: 'Legende',
    // --- i18n additions ---
    app_successfullyUpgradedTo: "Erfolgreich aktualisiert auf",
    app_successfullyDowngradedTo: "Erfolgreich herabgestuft auf",
    app_failedToChangeSubscription: "Abonnement konnte nicht geändert werden",
    app_unknownError: "Unbekannter Fehler",
    app_reportThankYou: "Danke für deine Meldung. Wir werden sie in Kürze prüfen.",
    app_reportAlreadyReported: "Dieser Ort wurde bereits gemeldet. Danke für deinen Hinweis!",
    app_reportError: "Beim Senden deiner Meldung ist ein Fehler aufgetreten. Bitte versuche es erneut.",
    app_away: "entfernt",
    app_hoursNotAvailable: "Öffnungszeiten nicht verfügbar",
    app_website: "Webseite",
    app_drive: "Fahren",
    app_geoUnableRetrieve: "Dein Standort konnte nicht ermittelt werden.",
    app_geoPermissionDenied: "Standortzugriff verweigert. Bitte aktiviere die Standortberechtigungen in deinem Browser.",
    app_geoUnavailable: "Standortinformationen nicht verfügbar. Bitte versuche es erneut.",
    app_geoTimeout: "Zeitüberschreitung bei der Standortabfrage. Bitte versuche es erneut.",
    app_geoErrorLabel: "Standortfehler",
    app_geoErrorTitle: "Standortfehler",
    app_geoDefaultLocationNote: "Der Raum Uppsala wird als Standardstandort verwendet. Bitte aktiviere die Standortberechtigungen für eine genaue Positionierung.",
    app_geoFailedGetLocation: "Dein Standort konnte nicht abgerufen werden. Bitte versuche es erneut.",
    app_geoNotSupported: "Geolokalisierung wird von deinem Browser nicht unterstützt.",
    app_tryAgainFewMinutes: "Bitte versuche es in ein paar Minuten erneut.",
    app_errorLabel: "Fehler",
    app_aboutWhere2: "Über Where2",
    app_aboutText: "Das Team hinter Where2 fährt seit über 10 Jahren Elektroautos und kennt die Herausforderung, unterwegs während des Ladens gute Essensmöglichkeiten zu finden, aus eigener Erfahrung. Zu oft kommen Fahrer an einem Supercharger an und sehen vom Parkplatz aus keine Restaurants – oder landen an Orten, die die Erwartungen nicht erfüllen. Where2 wurde entwickelt, um dieses Problem zu lösen: Wir helfen E-Auto-Fahrern, die besten Restaurants in der Nähe mit echten Bewertungen, Fotos und Fußweg-Beschreibungen zu entdecken, damit sie ihre Ladestopps optimal nutzen und ihre Reise genießen können.",
    app_welcomeBack: "Willkommen zurück",
    app_readyToFindFood: "Bereit, tolles Essen zu finden, während du lädst?",
    app_welcomeBackReady: "Willkommen zurück! Bereit, tolle Restaurants zu finden, während du lädst?",
    app_plusFeatureUpgrade: "Plus-Funktion – zum Upgraden klicken",
    app_getSuggestions: "Vorschläge erhalten",
    app_backToSuperchargerView: "Zurück zur Supercharger-Ansicht",
    app_closeMap: "Karte schließen",
    app_loadingMap: "Karte wird geladen...",
    app_loadingChargers: "Ladestationen werden geladen...",
    app_findingSuperchargers: "Supercharger in deiner Nähe werden gesucht",
    app_fetchingStations: "Ladestationen werden abgerufen...",
    app_slowConnectionNote: "Bei langsameren Verbindungen kann dies einen Moment dauern",
    app_zoomIn: "Vergrößern (auf deinen Standort zentriert)",
    app_zoomOut: "Verkleinern (auf deinen Standort zentriert)",
    app_chooseYourPreference: "Wähle deine Präferenz",
    app_prefNormal: "Normale Präferenzen",
    app_prefNicer: "Etwas gehobener",
    app_prefCheaper: "Etwas günstiger",
    app_searching: "Suche läuft...",
    app_startSearch: "Suche starten",
    app_blueCircleNote: "Wir suchen nach Restaurants in der Nähe von Ladestationen innerhalb des blauen Kreises",
    app_found: "Gefunden",
    app_restaurantsWord: "Restaurants",
    app_higherQuality: "(höhere Qualität)",
    app_budgetFriendly: "(preisgünstig)",
    app_unknownCharger: "Unbekannte Ladestation",
    app_seeAllRestaurants: "Alle Restaurants anzeigen",
    app_and: "und",
    app_moreRestaurants: "weitere Restaurants...",
    app_noRestaurantsInArea: "Keine Restaurants im ausgewählten Bereich gefunden. Versuche, herauszuzoomen oder die Karte zu verschieben.",
    app_loading: "Wird geladen...",
    app_close: "Schließen",
    app_learnTitle: "Die Zukunft des Reisens mit dem E-Auto",
    app_learnRealityTitle: "Die neue Realität des E-Auto-Ladens",
    app_learnRealityText: "Heutzutage hat sich die Ladezeit deutlich verkürzt, und um eine Reise optimal zu nutzen, ist es manchmal gut, bei der Ankunft an einer Ladestation gut auf das Essen vorbereitet zu sein. Mit dieser App kannst du die Restaurantwahl vorziehen, sodass sich schon vor der Ankunft an der Ladestation entscheiden lässt, welches am besten zu den Reisenden passt.",
    app_learnPreOrderTitle: "Plane deine Mahlzeit im Voraus",
    app_learnPreOrderText: "Warum nicht schon vor der Ankunft entscheiden, welches Restaurant das richtige ist? So wird dein Ladestopp von einer Wartezeit zu einem nahtlosen Teil deiner Reise. Kein hektisches Suchen nach Essen mehr und kein Zufriedengeben mit dem, was gerade da ist – du kannst deine perfekte Mahlzeit im Voraus planen.",
    app_learnHowTitle: "So funktioniert Where2",
    app_learnStep1Title: "Ladestationen finden:",
    app_learnStep1Text: "Nutze deinen Standort oder suche nach Ladestationen entlang deiner Route. Where2 unterstützt Tesla Superchargers, Ionity, OKQ8 und weitere Netzwerke.",
    app_learnStep2Title: "Restaurants entdecken:",
    app_learnStep2Text: "Sieh dir alle Restaurants in der Nähe mit Echtzeitinformationen an, darunter Bewertungen, Fotos, Öffnungszeiten und Fußweg-Beschreibungen von der Ladestation aus.",
    app_learnStep3Title: "Plane deinen Stopp:",
    app_learnStep3Text: "Vergleiche Optionen, prüfe Entfernungen und erhalte sogar Fußweg-Beschreibungen, um deine Ladezeit optimal zu nutzen.",
    app_learnVisionTitle: "Die Zukunftsvision",
    app_learnVisionText: "In Zukunft wird sich Where2 weiterentwickeln und Restaurantvorlieben, Ernährungsanforderungen und sogar die Integration mit Bestellsystemen von Restaurants umfassen. Stell dir vor, du könntest deine Essensvorlieben festlegen und Where2 schlägt dir anhand deiner kulinarischen Interessen die besten Ladestopps vor. Die App lernt deine Vorlieben mit der Zeit und macht jede Reise persönlicher und angenehmer.",
    app_learnBenefitsTitle: "Wichtigste Vorteile:",
    app_benefitTimeTitle: "Zeiteffizienz:",
    app_benefitTimeText: "Plane dein Essen vor der Ankunft, nicht danach",
    app_benefitChoicesTitle: "Bessere Auswahl:",
    app_benefitChoicesText: "Sieh dir alle Optionen vor der Ankunft an",
    app_benefitStressTitle: "Weniger Stress:",
    app_benefitStressText: "Kein hektisches Suchen nach Essen mehr",
    app_benefitJourneyTitle: "Bessere Reise:",
    app_benefitJourneyText: "Verwandle Ladestopps in angenehme Pausen",
    app_benefitFutureTitle: "Zukunftssicher:",
    app_benefitFutureText: "Plattform für Restaurant-Präferenzen und Entdeckung",
    login_title: "Anmelden",
    login_emailLabel: "E-Mail",
    login_emailPlaceholder: "Gib deine E-Mail-Adresse ein",
    login_passwordLabel: "Passwort",
    login_submit: "Anmelden",
    login_cancel: "Abbrechen",
    login_switchToRegister: "Noch kein Konto? Registrieren",
    register_title: "Registrieren",
    register_trialTitle: "Kostenlose Plus-Testversion!",
    register_trialTextBefore: "Erhalte",
    register_trialTextAfter: "Premium-Zugang mit allen freigeschalteten Funktionen. Nach Ablauf deiner Testversion wird dein Konto zu einem kostenlosen eingeschränkten Konto. Du kannst jederzeit auf ein kostenpflichtiges Abonnement upgraden, um die Premium-Funktionen zu behalten.",
    register_unitMinutes: "Minuten",
    register_unitHours: "Stunden",
    register_unitDays: "Tage",
    register_emailLabel: "E-Mail",
    register_emailPlaceholder: "Gib deine E-Mail-Adresse ein",
    register_displayNameLabel: "Anzeigename",
    register_displayNamePlaceholder: "Gib deinen Anzeigenamen ein",
    register_passwordLabel: "Passwort",
    register_confirmPasswordLabel: "Passwort bestätigen",
    register_couponLabel: "Gutscheincode (optional)",
    register_couponPlaceholder: "Gib einen Gutscheincode ein, falls vorhanden",
    register_submit: "Registrieren",
    register_cancel: "Abbrechen",
    register_switchToLogin: "Du hast bereits ein Konto? Anmelden",
    verify_errorNoToken: "Kein Verifizierungstoken angegeben",
    verify_successDefault: "E-Mail erfolgreich verifiziert!",
    verify_errorFailed: "Verifizierung fehlgeschlagen",
    verify_errorNetwork: "Netzwerkfehler. Bitte versuche es erneut.",
    verify_loadingTitle: "E-Mail wird verifiziert",
    verify_loadingText: "Bitte warte, während wir deine E-Mail-Adresse verifizieren...",
    verify_successTitle: "E-Mail verifiziert!",
    verify_continue: "Weiter zu Where2",
    verify_failedTitle: "Verifizierung fehlgeschlagen",
    verify_goHome: "Zur Startseite",
    verify_registerAgain: "Erneut registrieren",
    faq_close: "Schließen",
    faq_title: "Häufig gestellte Fragen",
    faq_q1Question: "Worum geht es hier eigentlich?",
    faq_q1Answer: "Einfach eine Möglichkeit, etwas besser auf deinen Ladestopp vorbereitet zu sein",
    faq_q2Question: "Ich verstehe es nicht! Was soll ich tun?",
    faq_q2Answer: "Kein Problem! Klicke auf der Startseite auf 'Zeig mir, wie es funktioniert' für eine kurze Anleitung – oder tippe einfach auf 'Restaurants finden', um die Ladestationen in deiner Nähe und die Restaurants in Gehweite jeder Station zu sehen.",
    faq_q3Question: "Ich verstehe es nicht. Ich sehe keine Restaurants!",
    faq_q3Answer: "Das könnte natürlich einer dieser lästigen Fehler sein, aber prüfe bitte auch, ob du die Einstellungen so konfiguriert hast, wie du sie haben möchtest. Hier kannst du deine Restaurantpräferenzen festlegen und wie weit du beim Laden zu gehen bereit bist. Du kannst außerdem Preisniveaus und andere Filteroptionen auswählen, die dir helfen, die beste Wahl für dich zu finden.",
    faq_q4Question: "Warum ist das nicht kostenlos? Es sollte ein Service sein!",
    faq_q4Answer: "Leider ist auch die von uns verwendete Datenquelle nicht kostenlos, was bedeutet, dass die Nutzung der Anwendung je nach Volumen manchmal einen hohen Preis hat. Wir versuchen jedoch, den Preis für die verschiedenen Optionen so gering wie möglich zu halten, um die Kosten auch bei hoher Auslastung zu decken.",
    faq_q5Question: "Warum gibt es verschiedene Abonnementstufen?",
    faq_q5AnswerPart1: "Wir halten es einfach: eine ",
    faq_freeTier: "Free",
    faq_q5AnswerPart2: " Stufe für die grundlegende Restaurantsuche und eine einzige kostenpflichtige ",
    faq_plusTier: "Plus",
    faq_q5AnswerPart3: " Stufe, die alles freischaltet — Restaurantfotos und Bewertungen, erweiterte Filterung, Routenoptimierung, die Funktion \"Mahlzeit vorschlagen\" und zusätzliche Suchtypen (Hotels, Shopping).",
    faq_q6Question: "Ist das nicht eigentlich ein Luxusproblem, das keine Lösung braucht?",
    faq_q6Answer: "Ja, natürlich, aber die Macher der Seite empfanden dies immer als Zeitdieb und Unannehmlichkeit, da Ladestandorte sehr unterschiedlich sind und sehr verschiedene Möglichkeiten bieten – vorbereitet zu sein ist also nie verkehrt!",
    faq_q7Question: "Wie soll ich das im Auto verwenden?",
    faq_q7Answer: "Wir arbeiten an einer CarPlay-Version. Hoffentlich ist sie bald verfügbar. In der Zwischenzeit empfehlen wir die Nutzung des Mobiltelefons oder ggf. eines Tablets. Einige E-Fahrzeuge haben eine integrierte Webbrowser-Funktion, und dort sollte es gut funktionieren. Denke daran, die Anwendung nicht während der Fahrt zu benutzen!",
    faq_q8Question: "Ich möchte auch Hotels und Einkaufsmöglichkeiten in der Nähe des Ladegeräts finden. Könnt ihr das nicht auch anzeigen?",
    faq_q8Answer: "Wir arbeiten auch an diesen Optionen. Bitte sieh im Neuigkeitenbereich nach Updates dazu.",
    faq_q9Question: "Habt ihr nicht eine schöne KI-Integration, bei der ich der Anwendung sagen kann, was ich möchte?",
    faq_q9Answer: "Wir haben einige Alternativen hierzu ausprobiert, aber festgestellt, dass die Ergebnisse bestenfalls unzuverlässig und meist einfach albern und ungenau sind. Deshalb haben wir den altmodischen Weg gewählt, um dir zu helfen, ein gutes Restaurant in der Nähe eines Ladegeräts zu finden.",
    faq_q10Question: "Woher kommen die Bewertungen?",
    faq_q10Answer: "Wir verwenden Google Places als Bewertungsquelle. Möglicherweise ändern wir dies in Zukunft. Vielleicht arbeiten wir sogar mit der Nutzerbasis der Anwendung für Bewertungen zusammen, aber vorerst sind die Google-Nutzer die Schiedsrichter.",
    faq_q11Question: "Was mache ich, wenn ich etwas finde, das nicht funktioniert?",
    faq_q11AnswerPart1: "Bitte ",
    faq_contactUsHere: "kontaktiere uns hier",
    faq_q11AnswerPart2: " bei allen Problemen, die auftreten. Wir sind hier, um zu helfen!",
    faq_q12Question: "Was mache ich, wenn ich eine neue Funktion möchte?",
    faq_q12AnswerPart1: "Genauso. Bitte ",
    faq_contactUs: "kontaktiere uns",
    faq_q12AnswerPart2: " und wir sehen, was wir tun können!",
    faq_q13Question: "Wie funktioniert Where2?",
    faq_q13Answer: "Where2 hilft E-Fahrern, Restaurants in der Nähe von Ladestationen zu finden. Klicke einfach auf \"Restaurant finden\", um Supercharger in der Nähe zu finden und Restaurantoptionen in Gehweite zu sehen.",
    faq_q14Question: "Welche Ladenetzwerke werden unterstützt?",
    faq_q14Answer: "Derzeit unterstützen wir Tesla Superchargers, Ionity und OKQ8. Du kannst in deinen Einstellungen auswählen, welche Netzwerke angezeigt werden.",
    faq_q15Question: "Wie weit kann ich nach Restaurants suchen?",
    faq_q15Answer: "Du kannst den Suchradius für Restaurants in deinen Einstellungen von 0,2 km bis 1,6 km anpassen. Der Standardwert beträgt 0,6 km für eine optimale Gehweite.",
    faq_q16Question: "Wie weit kann ich nach Superchargern suchen?",
    faq_q16Answer: "Du kannst den Suchradius für Supercharger in deinen Einstellungen von 20 km bis 400 km anpassen. Der Standardwert beträgt 100 km.",
    faq_q17Question: "Muss ich für die Nutzung von Where2 bezahlen?",
    faq_q17AnswerPart1: "Where2 hat eine ",
    faq_q17AnswerPart2: " Stufe plus ein kostenpflichtiges Abonnement: ",
    faq_q17AnswerPart3: " (3 USD monatlich oder 30 USD jährlich) schaltet Restaurantfotos und Bewertungen, erweiterte Filterung, Routenoptimierung, die Funktion \"Mahlzeit vorschlagen\" sowie die Hotel- & Shoppingsuche frei.",
    faq_q18Question: "Wie genau sind die Restaurantinformationen?",
    faq_q18Answer: "Wir verwenden die Google Places API, um Restaurantdaten in Echtzeit bereitzustellen, einschließlich Bewertungen, Fotos, Öffnungszeiten und Kontaktinformationen.",
    faq_q19Question: "Kann ich eine Fußweg-Wegbeschreibung zu Restaurants erhalten?",
    faq_q19Answer: "Ja! Klicke auf den Link \"🚶 Fußweg\" neben einem beliebigen Restaurant, um in Google Maps eine Fußweg-Wegbeschreibung vom Supercharger zum Restaurant zu erhalten.",
    faq_q20Question: "Werden meine Standortdaten gespeichert?",
    faq_q20Answer: "Nein, wir speichern deine Standortdaten nicht. Dein Standort wird nur vorübergehend verwendet, um Supercharger und Restaurants in der Nähe zu finden.",
    about_close: "Schließen",
    about_title: "Über Where2",
    about_body: "Das Team hinter Where2 ist seit über 10 Jahren E-Fahrzeugbesitzer und hat die Herausforderungen, unterwegs beim Laden gute Essensmöglichkeiten zu finden, aus erster Hand erlebt. Allzu oft kommen Fahrer an einem Supercharger an und sehen vom Parkplatz aus keine Restaurants – oder landen an Orten, die die Erwartungen nicht erfüllen. Where2 wurde geschaffen, um dieses Problem zu lösen: E-Fahrern zu helfen, die besten Restaurants in der Nähe mit echten Bewertungen, Fotos und Fußweg-Wegbeschreibungen zu entdecken, damit sie ihre Ladestopps optimal nutzen und ihre Reise genießen können.",
    learnMore_close: "Schließen",
    learnMore_title: "Die Zukunft des E-Fahrzeug-Reisens",
    learnMore_section1Title: "Die neue Realität des Ladens von E-Fahrzeugen",
    learnMore_section1Body: "Heutzutage hat sich die Ladezeit erheblich verkürzt, und um eine Reise optimal zu nutzen, ist es manchmal gut, gut vorbereitet zu sein, um bei der Ankunft an einem Ladestandort zu essen. Mit dieser App kannst du die Restaurantwahl vorverlegen, sodass bereits vor der Ankunft am Ladegerät entschieden werden kann, was den Reisenden am besten passt.",
    learnMore_section2Title: "Plane deine Mahlzeit im Voraus",
    learnMore_section2Body: "Warum eigentlich nicht schon vor der Ankunft entscheiden, welches Restaurant das richtige ist? So verwandelt sich dein Ladestopp von einer Wartezeit in einen nahtlosen Teil deiner Reise. Kein hektisches Suchen nach Essen mehr und kein Zufriedengeben mit dem, was gerade verfügbar ist – du kannst deine perfekte Mahlzeit im Voraus planen.",
    learnMore_section3Title: "So funktioniert Where2",
    learnMore_step1Label: "Ladestationen finden:",
    learnMore_step1Body: " Nutze deinen Standort oder suche nach Ladestationen entlang deiner Route. Where2 unterstützt Tesla Superchargers, Ionity, OKQ8 und andere Netzwerke.",
    learnMore_step2Label: "Restaurants entdecken:",
    learnMore_step2Body: " Sieh alle Restaurants in der Nähe mit Echtzeitinformationen wie Bewertungen, Fotos, Öffnungszeiten und Fußweg-Wegbeschreibungen vom Ladegerät aus.",
    learnMore_step3Label: "Plane deinen Stopp:",
    learnMore_step3Body: " Vergleiche Optionen, prüfe Entfernungen und erhalte sogar Fußweg-Wegbeschreibungen, um deine Ladezeit optimal zu nutzen.",
    learnMore_benefitsTitle: "Wichtigste Vorteile:",
    learnMore_benefit1Label: "Zeiteffizienz:",
    learnMore_benefit1Body: " Plane deine Mahlzeit vor der Ankunft, nicht danach",
    learnMore_benefit2Label: "Bessere Auswahl:",
    learnMore_benefit2Body: " Sieh alle Optionen vor der Ankunft",
    learnMore_benefit3Label: "Stressreduzierung:",
    learnMore_benefit3Body: " Kein hektisches Suchen nach Essen mehr",
    learnMore_benefit4Label: "Reiseaufwertung:",
    learnMore_benefit4Body: " Verwandle Ladestopps in angenehme Pausen",
    learnMore_benefit5Label: "Zukunftssicher:",
    learnMore_benefit5Body: " Plattform für Restaurant-Präferenzen und Entdeckung",
    contact_close: "Schließen",
    contact_title: "Kontaktiere uns",
    contact_subtitle: "Hast du eine Frage? Wir freuen uns, von dir zu hören!",
    contact_nameLabel: "Name *",
    contact_namePlaceholder: "Dein Name",
    contact_emailLabel: "E-Mail *",
    contact_emailPlaceholder: "your.email@example.com",
    contact_subjectLabel: "Betreff *",
    contact_subjectPlaceholder: "Worum geht es bei deiner Frage?",
    contact_messageLabel: "Nachricht *",
    contact_messagePlaceholder: "Erzähle uns mehr über deine Frage...",
    contact_successMessage: "Danke! Deine Nachricht wurde erfolgreich gesendet. Wir melden uns bald bei dir.",
    contact_errorMessage: "Entschuldigung, beim Senden deiner Nachricht ist ein Fehler aufgetreten. Bitte versuche es erneut.",
    contact_cancel: "Abbrechen",
    contact_sending: "Wird gesendet...",
    contact_sendMessage: "Nachricht senden",
    cookie_title: "Cookie-Hinweis",
    cookie_body1: "Wir verwenden Cookies, um deine authentifizierte Sitzung während der Nutzung unseres Dienstes aufrechtzuerhalten. Diese Cookies sind unerlässlich, damit die Website ordnungsgemäß funktioniert und dir ein sicheres Erlebnis bietet.",
    cookie_importantLabel: "Wichtig:",
    cookie_importantText: " Wir verwenden keine Cookies, um dich zwischen Websites zu verfolgen, oder für seitenübergreifende Tracking-Aktivitäten. Unsere Cookies beschränken sich strikt auf die Sitzungsverwaltung und Authentifizierung auf unserer Plattform.",
    cookie_storeLabel: "Was wir speichern:",
    cookie_storeText: " Nur Sitzungstokens für die Anmeldeauthentifizierung",
    cookie_dontLabel: "Was wir nicht tun:",
    cookie_dontText: " Seitenübergreifendes Tracking, Werbung oder Datenweitergabe",
    cookie_acceptCookies: "Cookies akzeptieren",
    cookie_decline: "Ablehnen",
    cookie_rememberNote: "Deine Wahl wird für zukünftige Besuche gespeichert",
    disclaimer_close: "Schließen",
    disclaimer_title: "Sicherheitshinweis",
    disclaimer_safetyBody: "Bitte beachte, dass diese Funktion vom Fahrer nicht verwendet werden darf, wenn das Fahrzeug nicht geparkt ist. Sie sollte von einem Beifahrer oder bei stehendem Fahrzeug genutzt werden. Die Nutzung dieser App/Website ist ausschließlich auf sichere Bedingungen beschränkt, und es wird keine Haftung übernommen, wenn diese Einschränkungen oder andere unsichere Nutzungsbedingungen nicht eingehalten werden.",
    nav_manageSubscription: "Klicken, um dein Abonnement zu verwalten",
    nav_mapCache: "Karten-Cache",
    nav_tiles: "Kacheln",
    nav_saved: "gespart",
    nav_signOut: "Abmelden",
    mainBtn_plusFeatureUpgrade: "Plus-Funktion – Zum Upgraden klicken",
    legend_stalls: "Plätze",
    legend_naStalls: "Plätze k. A.",
    legend_fromYourLocation: "von deinem Standort",
    legend_drive: "Fahrt",
    legend_arriveAt: "Ankunft um",
    legend_favoriteRestaurant: "Lieblingsrestaurant",
    map_favoriteChargerTooltip: "Ein Lieblingsrestaurant ist in der Nähe",
    legend_hoursNotAvailable: "Öffnungszeiten nicht verfügbar",
    legend_priceBudget: "Günstig",
    legend_priceModerate: "Mittel",
    legend_priceExpensive: "Teuer",
    legend_priceVeryExpensive: "Sehr teuer",
    legend_reviews: "Bewertungen",
    legend_web: "Web",
    legend_call: "Anrufen",
    legend_directions: "Route",
    legend_reportTooltip: "Melden, wenn dies kein Restaurant ist",
    legend_notARestaurant: "Kein Restaurant!",
    floatLegend_stalls: "Plätze",
    floatLegend_naStalls: "Plätze k. A.",
    floatLegend_openInGoogleMaps: "In Google Maps öffnen",
    floatLegend_maps: "Karten",
    floatLegend_closeLegend: "Legende schließen",
    floatLegend_selectedCharger: "Ausgewählter Lader",
    popup_closePopup: "Popup schließen",
    popup_supercharger: "Supercharger",
    popup_chargingStation: "Ladestation",
    popup_restaurantsFoundNearby: "Restaurants in der Nähe gefunden",
    popup_loginToSeeDetails: "Anmelden, um Restaurantdetails zu sehen",
    popup_loadingRestaurants: "Restaurants werden geladen...",
    restCard_away: "entfernt",
    restCard_hoursNotAvailable: "Öffnungszeiten nicht verfügbar",
    restCard_website: "Webseite",
    restCard_call: "Anrufen",
    restCard_directions: "Route",
    restCard_teslaNav: "Tesla Nav",
    proxActions_wait1h: "1 Std. warten",
    proxActions_wait2h: "2 Std. warten",
    proxActions_turnOffAll: "Alle Benachrichtigungen ausschalten",
    proxNotif_away: "entfernt",
    proxNotif_greatRestaurantsNearby: "tolle Restaurants in der Nähe - perfekt für eine Essenspause!",
    proxNotif_topRestaurants: "Top-Restaurants:",
    proxNotif_clicked: "Geklickt!",
    proxNotif_showMe: "Zeigen",
    proxNotif_close: "Schließen",
    chargerSearch_min3chars: "Bitte mindestens 3 Zeichen zum Suchen eingeben",
    chargerSearch_searchFailed: "Suche fehlgeschlagen",
    chargerSearch_locationSearchFailed: "Standortsuche fehlgeschlagen",
    chargerSearch_chargerSearchFailed: "Ladersuche fehlgeschlagen",
    chargerSearch_unknown: "Unbekannt",
    chargerSearch_title: "Lader suchen",
    chargerSearch_placeholder: "Lader nach Stadt oder Ort suchen (z. B. Stockholm, Sollentuna)...",
    chargerSearch_typeMin3: "Mindestens 3 Zeichen zum Suchen eingeben",
    chargerSearch_searchingAround: "Suche in der Nähe von:",
    chargerSearch_noChargersFound: "Keine Lader gefunden für",
    chargerSearch_tryDifferent: "Versuche, nach einer anderen Stadt oder einem anderen Ort zu suchen",
    chargerSearch_na: "k. A.",
    chargerSearch_chargingPoints: "Ladepunkte",
    chargerSearch_more: "mehr",
    chargerSearch_selectCharger: "Lader auswählen",
    chargerSearch_findPerfectStop: "Finde deinen perfekten Ladestopp",
    chargerSearch_emptyStateDesc: "Suche Lader nach Stadt oder Ort, um die besten Orte zum Laden und Essen zu finden.",
    report_title: "Kein Restaurant melden",
    report_aboutGoogleData: "Über Google-Places-Daten",
    report_para1: "Die Google-API stuft manchmal Betriebe, die keine Restaurants sind (wie Tankstellen, Geschäfte oder Dienstleister), als Restaurants ein. Das kann passieren, wenn diese Betriebe Essen als Nebenleistung anbieten.",
    report_para2: "Wenn du glaubst, dass dieser Ort nicht in erster Linie ein Restaurant ist, teile es uns bitte mit, damit wir unsere Empfehlungen verbessern können.",
    report_reasonLabel: "Warum ist dies kein Restaurant? (Optional)",
    report_reasonPlaceholder: "z. B. 'Das ist eine Tankstelle, die nur Snacks verkauft' oder 'Das ist ein Lebensmittelgeschäft, kein Restaurant'",
    report_cancel: "Abbrechen",
    report_submitting: "Wird gesendet...",
    report_submit: "Bericht senden",
    mealPlan_south: "SÜDEN",
    mealPlan_north: "NORDEN",
    mealPlan_latitudeStable: "BREITENGRAD STABIL",
    mealPlan_west: "WESTEN",
    mealPlan_east: "OSTEN",
    mealPlan_title: "MAHLZEITENPLAN ERSTELLT!",
    mealPlan_close: "Schließen",
    mealPlan_locationHistory: "STANDORTVERLAUF",
    mealPlan_travelDirection: "REISERICHTUNG",
    mealPlan_vector: "Vektor:",
    mealPlan_direction: "Richtung:",
    mealPlan_superchargersInDirection: "SUPERCHARGER IN RICHTUNG",
    mealPlan_foundInDirection: "In Reiserichtung gefunden:",
    mealPlan_selectedForPlan: "Für Mahlzeitenplan ausgewählt:",
    mealPlan_top: "Top",
    mealPlan_byDistance: "nach Entfernung",
    mealPlan_mealPlanHeading: "MAHLZEITENPLAN",
    mealPlan_away: "entfernt",
    mealPlan_minTravelTime: "Min. Fahrzeit",
    mealPlan_fromCharger: "vom Lader",
    mealPlan_hoursNotAvailable: "Öffnungszeiten nicht verfügbar",
    mealPlan_fromCache: "(aus dem Cache)",
    mealPlan_noRestaurantsFound: "Keine Restaurants gefunden",
    mealPlan_noRestaurantsDesc: "An den ausgewählten Superchargern wurden keine Restaurants gefunden.",
    mealPlan_jensTestMode: "JENS-TESTMODUS AKTIV",
    mealPlan_simulatedTravel: "Simulierte Fahrt nach Süden: Stockholm → Süden",
    suggestMeal_waitForMap: "Bitte warte, bis die Karte vollständig geladen ist, bevor du suchst.",
    suggestMeal_noChargers: "Keine Ladestationen im Gebiet oder in der Nähe gefunden. Versuche einen anderen Ort oder zoome weiter heraus.",
    suggestMeal_searchFailed: "Suche fehlgeschlagen. Bitte versuche es erneut.",
    suggestMeal_prefNormal: "Normale Vorlieben",
    suggestMeal_prefNicer: "Etwas gehobener",
    suggestMeal_prefCheaper: "Etwas günstiger",
    suggestMeal_descNormal: "Ausgewogene Empfehlungen basierend auf Bewertungen",
    suggestMeal_descNicer: "Premium-Lader und Restaurants mit Bewertungen ab 4,0",
    suggestMeal_descCheaper: "Preiswerte Optionen mit Bewertungen ab 3,5",
    suggestMeal_title: "Bereich für Mahlzeitenvorschläge auswählen",
    suggestMeal_close: "Schließen",
    suggestMeal_fromYourLocation: "von deinem Standort",
    suggestMeal_estimated: "(geschätzt)",
    suggestMeal_minDrive: "Min. Fahrt von hier",
    suggestMeal_arriveAt: "Ankunft um",
    suggestMeal_backToSearch: "Zurück zum Suchbereich",
    suggestMeal_loadingMap: "Karte wird geladen...",
    suggestMeal_searching: "Suche läuft...",
    suggestMeal_startSearch: "Suche starten",
    suggestMeal_dragInstruction: "Ziehe die Karte, um den blauen Kreis über deinem gewünschten Suchbereich zu platzieren, und klicke dann auf Start",
    miniMap_close: "Schließen",
    miniMap_map: "Karte:",
    miniMap_sendToTesla: "An Tesla-App senden",
    miniMap_teslaNavigation: "Tesla-Navigation",
    miniMap_nav: "Nav",
    miniMap_googleMapsNav: "Google-Maps-Navigation",
    miniMap_maps: "Karten",
    miniMap_drive: "Fahren",
    settings_title: "Einstellungen",
    settings_subtitle: "Passe dein Erlebnis an",
    settings_restaurantRadius: "Suchradius für Restaurants",
    settings_chargerRadius: "Suchradius für Ladestationen",
    settings_chargerNetworks: "Ladenetzwerke",
    settings_unitSystem: "Einheitensystem",
    settings_metric: "Metrisch (km, °C)",
    settings_imperial: "Imperial (mi, °F)",
    settings_save: "Einstellungen speichern",
    settings_cancel: "Abbrechen",
    settings_profileSettings: "Profileinstellungen",
    settings_emailAddress: "E-Mail-Adresse",
    settings_emailPlaceholder: "E-Mail-Adresse",
    settings_fullName: "Vollständiger Name",
    settings_fullNamePlaceholder: "Gib deinen vollständigen Namen ein",
    settings_searchPreferences: "Sucheinstellungen",
    settings_current: "Aktuell",
    settings_evChargingNetworks: "Ladenetzwerke für E-Autos",
    settings_displayPreferences: "Anzeigeeinstellungen",
    settings_metricUnits: "Metrische Einheiten",
    settings_usingMetric: "Kilometer und Celsius werden verwendet",
    settings_usingImperial: "Meilen und Fahrenheit werden verwendet",
    settings_currencyUSD: "US-Dollar",
    settings_currencyEUR: "Euro",
    settings_currencySEK: "Schwedische Krone",
    settings_currencyDKK: "Dänische Krone",
    settings_currencyNOK: "Norwegische Krone",
    settings_currencyCHF: "Schweizer Franken",
    settings_currency: "Währung",
    settings_foodPreferences: "Essensvorlieben",
    settings_priceLevelsChains: "Preisniveaus & Ketten",
    settings_subscription: "Abonnement",
    settings_manageYourPlan: "Verwalte deinen Tarif",
    settings_saving: "Wird gespeichert...",
    prefs_close: "Schließen",
    prefs_title: "Restaurant-Einstellungen",
    prefs_loading: "Einstellungen werden geladen...",
    prefs_preferredChains: "Bevorzugte Restaurantketten",
    prefs_loadingChains: "Restaurantketten werden geladen...",
    prefs_customKeywords: "Eigene Schlüsselwörter",
    prefs_keywordPlaceholder: "z. B. Hamburger, Pizza, Sushi...",
    prefs_add: "Hinzufügen",
    prefs_priceLevels: "Preisniveaus",
    prefs_priceLevelsDesc: "Wähle die Preisklassen, die du für Restaurants bevorzugst",
    prefs_price1_label: "Preisgünstig",
    prefs_price1_desc: "Günstig ($)",
    prefs_price2_label: "Mittel",
    prefs_price2_desc: "Mittel ($$)",
    prefs_price3_label: "Teuer",
    prefs_price3_desc: "Teuer ($$$)",
    prefs_price4_label: "Sehr teuer",
    prefs_price4_desc: "Sehr teuer ($$$$)",
    prefs_searchConfiguration: "Suchkonfiguration",
    prefs_searchLevel_include_all_name: "Alle Ladestationen einschließen (Standard)",
    prefs_searchLevel_include_all_desc: "Alle Ladestationen anzeigen, bevorzugte Restaurants mit goldenem Rahmen hervorheben",
    prefs_searchLevel_show_favorites_only_name: "Nur meine Lieblingsrestaurants anzeigen",
    prefs_searchLevel_show_favorites_only_desc: "Alle Ladestationen anzeigen, aber nur Restaurants, die den Vorlieben entsprechen",
    prefs_searchLevel_favorites_only_chargers_name: "Nur Ladestationen mit Lieblingsrestaurants",
    prefs_searchLevel_favorites_only_chargers_desc: "Nur Ladestationen anzeigen, in deren Nähe Restaurants den Vorlieben entsprechen",
    prefs_savedSuccess: "Einstellungen erfolgreich gespeichert!",
    prefs_saving: "Wird gespeichert...",
    prefs_savePreferences: "Einstellungen speichern",
    prefs_cancel: "Abbrechen",
    prefs_errorLoad: "Einstellungen konnten nicht geladen werden",
    prefs_errorSave: "Einstellungen konnten nicht gespeichert werden",
    prefs_unknownError: "Ein unbekannter Fehler ist aufgetreten",
    restCount_foundSingular: "Restaurant gefunden",
    restCount_foundPlural: "Restaurants gefunden",
    restCount_near: "in der Nähe von",
    restCount_signupPrompt: "Registriere dich, um Restaurantdetails, Fotos, Bewertungen und mehr zu sehen!",
    restCount_unlockFullDetails: "Alle Details freischalten",
    restCount_freeToSignup: "Kostenlose Registrierung • Plus-Funktionen verfügbar",
    restCount_plusFeaturesInclude: "Plus-Funktionen umfassen:",
    restCount_featureRatings: "Restaurantbewertungen & Rezensionen",
    restCount_featurePrices: "Preisniveaus & Küchentypen",
    restCount_featureNavigation: "Navigation & Wegbeschreibung",
    pricing_errorCalculating: "Fehler bei der Berechnung der Abonnementänderung: ",
    pricing_failedCalculate: "Abonnementänderung konnte nicht berechnet werden",
    pricing_changeSuccess: "Abonnement erfolgreich geändert!",
    pricing_errorChanging: "Fehler beim Ändern des Abonnements: ",
    pricing_failedChange: "Abonnement konnte nicht geändert werden",
    pricing_plusPlanTitle: "Plus-Tarif",
    pricing_plusPlanSubtitle: "Alles, was du für E-Auto-Reisen brauchst",
    pricing_close: "Schließen",
    pricing_choosePlan: "Wähle deinen Tarif",
    pricing_pricesShownIn: "Preise angezeigt in",
    pricing_mostPopular: "Am beliebtesten",
    pricing_monthly: "monatlich",
    pricing_yearly: "jährlich",
    pricing_save: "Spare",
    pricing_month: "Monat",
    pricing_year: "Jahr",
    pricing_monthBilledYearly: "Monat bei jährlicher Abrechnung",
    pricing_activePlan: "Aktiver Tarif",
    pricing_calculating: "Wird berechnet...",
    pricing_processing: "Wird verarbeitet...",
    pricing_changePlan: "Tarif ändern",
    pricing_choose: "Wählen",
    pricing_currentSubStatus: "Aktueller Abonnementstatus",
    pricing_hideHistory: "Verlauf ausblenden",
    pricing_viewHistory: "Verlauf anzeigen",
    pricing_planLabel: "Tarif:",
    pricing_statusLabel: "Status:",
    pricing_expiresLabel: "Läuft ab:",
    pricing_recentBillingHistory: "Aktueller Abrechnungsverlauf",
    pricing_noBillingHistory: "Noch kein Abrechnungsverlauf.",
    pricing_confirmSubChange: "Abonnementänderung bestätigen",
    pricing_currentPlanLabel: "Aktueller Tarif:",
    pricing_newPlanLabel: "Neuer Tarif:",
    pricing_daysUsed: "Genutzte Tage:",
    pricing_of: "von",
    pricing_daysRemaining: "Verbleibende Tage:",
    pricing_proratedRefund: "Anteilige Rückerstattung:",
    pricing_noCharge: "Keine Gebühr",
    pricing_netCharge: "Nettobetrag",
    pricing_descriptionLabel: "Beschreibung:",
    pricing_cancel: "Abbrechen",
    pricing_confirmChange: "Änderung bestätigen",
    subMgmt_title: "Abonnementverwaltung",
    subMgmt_currentSubscription: "Aktuelles Abonnement",
    subMgmt_monthly: "Monatlich",
    subMgmt_yearly: "Jährlich",
    subMgmt_statusLabel: "Status:",
    subMgmt_expiresLabel: "Läuft ab:",
    subMgmt_priceLabel: "Preis:",
    subMgmt_choosePlan: "Wähle deinen Tarif",
    subMgmt_plusPlans: "Plus-Tarife",
    subMgmt_currentPlan: "Aktueller Tarif",
    subMgmt_save: "Spare",
    subMgmt_month: "Monat",
    subMgmt_select: "Auswählen",
    subMgmt_footer: "Alle Tarife beinhalten Zugang zu unserem Netzwerk an E-Auto-Ladestationen sowie Funktionen zur Restaurantsuche. Kündige oder ändere deinen Tarif jederzeit.",
    tierChange_upgrade: "Upgrade",
    tierChange_downgrade: "Downgrade",
    tierChange_subscription: "Abonnement",
    tierChange_importantNotice: "Wichtiger Hinweis",
    tierChange_downgradeWarning: "Ein Downgrade deines Abonnements reduziert die verfügbaren Funktionen. Einige Funktionen sind möglicherweise unmittelbar nach der Änderung nicht mehr verfügbar.",
    tierChange_aboutToUpgrade: "Du bist dabei, ein Upgrade durchzuführen von:",
    tierChange_aboutToDowngrade: "Du bist dabei, ein Downgrade durchzuführen von:",
    tierChange_month: "Monat",
    tierChange_monthlyPriceChange: "Monatliche Preisänderung:",
    tierChange_upgradeBillingInfo: "Dein Abonnement wird sofort aufgewertet. Dir wird die anteilige Differenz für den aktuellen Abrechnungszeitraum berechnet.",
    tierChange_downgradeBillingInfo: "Dein Abonnement wird sofort herabgestuft. Ein nicht genutzter Anteil wird deinem nächsten Abrechnungszeitraum gutgeschrieben.",
    tierChange_cancel: "Abbrechen",
    tierChange_processing: "Wird verarbeitet...",
    tierChange_confirm: "Bestätigen",
    // Navigation
    login: 'Anmelden',
    register: 'Registrieren',
    logout: 'Abmelden',
    settings: 'Einstellungen',
    about: 'Über',
    pricing: 'Preise',
    faq: 'FAQ',
    contact: 'Kontakt',
    
    // Hero Section
    heroTitle: 'Finde die beste Mahlzeit in der Nähe deines E-Auto-Laders',
    heroSubtitle: 'Entdecke top-bewertete Restaurants in Gehweite deiner Ladestation. Where2 hilft dir dabei, deine Mittagspause bei Superchargern zu planen und zeigt dir die besten Orte zum Essen in der Nähe—gefiltert nach deinen Präferenzen und Bewertungen—damit du eine gute Mahlzeit genießen kannst, während dein Auto lädt.',
    getStarted: 'Los geht\'s!',
    learnMore: 'Zeig mir, wie es funktioniert',
    
    // Features
    smartLocationSearch: 'Ladegeräte suchen',
    smartLocationDesc: 'Suche nach Ladestationen nach Standort und finde tolle Restaurants in der Nähe.',
    searchChargers: 'Ladegeräte suchen',
    preOrderReady: 'Essensvorschläge',
    preOrderDesc: 'Nutze diese Funktion, um auf der Karte den Bereich auszuwählen, zu dem du unterwegs bist und für den du Restaurantvorschläge nach deinen Präferenzen erhalten möchtest.',
    restaurantDiscovery: 'Restaurant-Entdeckung',
    restaurantDesc: 'Entdecke neue Essenserfahrungen mit detaillierten Bewertungen und Fotos.',
    
    // CTA Section
    ctaTitle: 'Bereit, deinen nächsten Lieblingsort zum Essen zu finden?',
    ctaSubtitle: 'Plane deine Mahlzeit, bevor du ankommst.',
    
    // Success Stories
    successStories: 'Erfolgsgeschichten',
    successStoriesDesc: 'Sieh, wie Where2 die E-Auto-Essenserfahrung verändert',
    shareYourStory: 'Teile deine Geschichte',
    
    // Main Button
    findNearbyRestaurants: 'Restaurants finden',
    planMyMeal: '🚗 Plane meine Mahlzeit',
    
    // Modals
    email: 'E-Mail',
    password: 'Passwort',
    confirmPassword: 'Passwort bestätigen',
    couponCode: 'Gutscheincode (Optional)',
    enterEmail: 'Gib deine E-Mail-Adresse ein',
    enterCoupon: 'Gib Gutscheincode ein, falls du einen hast',
    dontHaveAccount: 'Hast du kein Konto? Registrieren',
    
    // Settings
    yourLocationIcon: 'Dein Standort-Icon',
    priceRangePreferences: 'Preisbereich-Präferenzen',
    maxRestaurants: 'Max. Restaurants anzeigen',
    
    // Restaurant Popup
    nearestRestaurants: 'Nächstgelegene Restaurants:',
    open: 'Geöffnet',
    closed: 'Geschlossen',
    km: 'km',
    call: 'Anrufen',
    directions: 'Wegbeschreibung',
    map: 'Karte',
    showMap: 'Karte mit Restaurants anzeigen',
    close: 'Schließen',
    
    // Disclaimer
    disclaimerTitle: 'Sicherheitshinweis',
    disclaimerText: 'Bitte beachte, dass diese Funktion nicht vom Fahrer genutzt werden darf, wenn das Fahrzeug nicht geparkt ist. Sie sollte von einem Beifahrer oder bei stehendem Fahrzeug genutzt werden. Die Nutzung dieser App-Website ist vollständig auf sichere Bedingungen beschränkt und es wird keine Haftung akzeptiert, wenn diese Einschränkungen oder andere unsichere Nutzungsbedingungen nicht eingehalten werden.',
    iUnderstand: 'Ich verstehe - Fortfahren',
    doNotShowAgain: 'Diesen Hinweis nicht mehr anzeigen',
    importantSafetyNotice: 'Wichtiger Sicherheitshinweis',
    safeUsageGuidelines: 'Sichere Nutzungsrichtlinien:',
    onlyUseWhenParked: 'Nur bei geparktem Fahrzeug verwenden: Stelle sicher, dass dein Fahrzeug vollständig gestoppt und geparkt ist',
    passengerUse: 'Passagiernutzung: Lass Passagiere die App während der Fahrt bedienen',
    safeEnvironment: 'Sichere Umgebung: Verwende nur in sicheren, gut beleuchteten Bereichen',
    followTrafficLaws: 'Verkehrsgesetze befolgen: Halte dich immer an lokale Verkehrsvorschriften',
    stayAlert: 'Wachsam bleiben: Bleibe dir deiner Umgebung bewusst',
    cancel: 'Abbrechen',
    disclaimerAcknowledgment: 'Durch das Fortfahren bestätigst du, dass du diesen Sicherheitshinweis gelesen und verstanden hast und zustimmst, die App verantwortungsvoll zu nutzen.',
    settingsNote: 'Du kannst diese Einstellung später in deinen Kontoeinstellungen ändern.',
    browserNote: 'Diese Präferenz wird in deinem Browser gespeichert.',
    
    // Learn More
    learnMoreTitle: 'Über Where2',
    learnMoreText: 'Heutzutage hat sich die Ladezeit erheblich reduziert und um das Beste aus einer Reise herauszuholen, ist es manchmal gut, gut vorbereitet zu sein, um zu essen, wenn man an einem Ladeort ankommt. Diese App ermöglicht es dir, die Restaurant-Überlegung zu verschieben, damit entschieden werden kann, welches am besten zu den Reisenden passt, noch bevor man am Lader ankommt. So kannst du deine perfekte Mahlzeit im Voraus planen... und dann mehr technische Beschreibung darüber, wie die App funktioniert und dass es in Zukunft möglich sein wird, Restaurant-Präferenzen und Ähnliches hinzuzufügen, um die besten Lader zu finden',
    
    // Contact
    contactTitle: 'Kontaktiere uns',
    contactDesc: 'Hast du eine Frage oder Feedback? Wir würden gerne von dir hören!',
    name: 'Name',
    subject: 'Betreff',
    message: 'Nachricht',
    sendMessage: 'Nachricht senden',
    enterName: 'Gib deinen Namen ein',
    enterSubject: 'Gib den Betreff ein',
    enterMessage: 'Gib deine Nachricht ein',
    messageSent: 'Nachricht erfolgreich gesendet!',
    messageError: 'Fehler beim Senden der Nachricht. Versuche es erneut.',
    sending: 'Sende...',
    
    // Auth Errors
    emailPasswordRequired: 'E-Mail und Passwort erforderlich',
    invalidEmail: 'Bitte gib eine gültige E-Mail-Adresse ein',
    passwordLength: 'Passwort muss zwischen 6 und 128 Zeichen lang sein',
    passwordsDontMatch: 'Passwörter stimmen nicht überein',
    userExists: 'Benutzer existiert bereits',
    invalidCredentials: 'Ungültige E-Mail oder Passwort',
    
    // Trial
    trialActive: 'Testphase aktiv',
    trialExpired: 'Testphase abgelaufen',
    trialDaysLeft: 'Tage übrig',
    trialHoursLeft: 'Stunden übrig',
    
    // Subscription
    subscription: 'Abonnement',
    free: 'Kostenlos',
    premium: 'Plus',
    expired: 'Abgelaufen',
    active: 'Aktiv',
    
    // General
    loading: 'Lade...',
    error: 'Fehler',
    success: 'Erfolg',
    save: 'Speichern',
    ok: 'OK',
    yes: 'Ja',
    no: 'Nej',
  }
};

// Language detection
export function detectLanguage(): Language {
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('sv')) return 'sv';
  if (browserLang.startsWith('es')) return 'es';
  if (browserLang.startsWith('de')) return 'de';
  return 'en';
}
